/* eslint-disable @next/next/link-passhref */
import React, { useEffect, Fragment, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useRecoilState, useRecoilValue } from "recoil";
import Tippy from "@tippyjs/react";
import { motion } from "framer-motion";
import clsx from "clsx";

import "tippy.js/dist/tippy.css";
import "tippy.js/themes/light.css";

import {
  engageDepartmentState,
  engageTimeSpanState,
  engageAvailableDeps,
  engageUpcomingMeetings,
} from "../../atoms/engage";
import { engageState } from "../../atoms/platform";
import { apiGetUpcoming } from "../../lib/api/engage";
import useEngageFilter from "../../lib/hooks/useEngageFilter";

import { Transition, Listbox } from "@headlessui/react";
import { SelectorIcon, CheckIcon } from "@heroicons/react/solid";
import { MdFilterList } from "react-icons/md";
import { HiOutlineDocumentDuplicate } from "react-icons/hi";

// import Loading from "../Shared/Loading";
import Supplemental from "./Supplemental";
import UserWayWidget from "../UserWayWidget";

const dates = [
  { name: "2027", id: "2027" },
  { name: "2026", id: "2026" },
  { name: "2025", id: "2025" },
  { name: "2024", id: "2024" },
  { name: "2023", id: "2023" },
  { name: "2022", id: "2022" },
  { name: "2021", id: "2021" },
  { name: "2020", id: "2020" },
  { name: "2019", id: "2019" },
  { name: "2018", id: "2018" },
  { name: "2017", id: "2017" },
  { name: "2016", id: "2016" },
  { name: "2015", id: "2015" },
  { name: "2014", id: "2014" },
  { name: "2013", id: "2013" },
  { name: "2012", id: "2012" },
  { name: "2011", id: "2011" },
  { name: "2010", id: "2010" },
  { name: "2009", id: "2009" },
  { name: "2008", id: "2008" },
  { name: "2007", id: "2007" },
  { name: "2006", id: "2006" },
  { name: "2005", id: "2005" },
];

const Agendas = () => {
  const router = useRouter();
  const { client, board } = router.query;
  const { applyFilter, filteredMeetings, fetching, filterText } =
    useEngageFilter();
  const platform = useRecoilValue(engageState);
  const selected = useRecoilValue(engageDepartmentState);
  const selectedDate = useRecoilValue(engageTimeSpanState);
  const departments = useRecoilValue(engageAvailableDeps);
  const [upcomingList, setUpcomingList] = useRecoilState(
    engageUpcomingMeetings,
  );
  const [supplemental, setSupplemental] = useState({
    visible: false,
    meeting: null,
  });

  useEffect(() => {
    if (client) {
      async function fetchData(department) {
        const upcoming = await apiGetUpcoming(client);
        setUpcomingList(upcoming);
        applyFilter({
          type: "FETCH_DATA",
          payload: { client, date: selectedDate.id, department, board },
        });
      }
      if (board) {
        if (filteredMeetings?.length === 0) {
          fetchData(board);
        }
      } else {
        if (filteredMeetings?.length === 0) {
          fetchData("all");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  async function handleChange(type, e) {
    if (type === "department") {
      applyFilter({ type: "SET_BOARD", payload: e, client });
    } else {
      applyFilter({ type: "SET_DATE", payload: e, client });
    }
  }

  return (
    <div className="py-4">
      {upcomingList?.length > 0 && (
        <>
          <p className="font-bold">Upcoming Meetings:</p>
          {/* Upcoming Meetings */}
          {/* Data Table */}
          <div className="hidden xl:flex xl:flex-col mt-2 mb-6">
            <div className="-my-2 overflow-x-auto sm:-mx-6 2xl:-mx-8">
              <div className="py-2 align-middle inline-block min-w-full px-2 2xl:px-8">
                <div className="shadow overflow-hidden border border-gray-400 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Meeting Type
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Date / Time
                        </th>

                        <th scope="col" className="relative px-6 py-3">
                          <span className="sr-only">Details</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {upcomingList?.map((meeting, personIdx) => {
                        if (board) {
                          if (board === meeting.department._id) {
                            return (
                              <tr
                                key={meeting.id}
                                className={
                                  personIdx % 2 === 0
                                    ? "bg-white"
                                    : "bg-gray-100"
                                }
                              >
                                <td className="px-2 2xl:px-6 py-4 text-sm font-medium text-gray-900">
                                  <div>{meeting.meetingType}</div>
                                  <div>{meeting.room.roomName}</div>
                                </td>
                                <td className="px-2 2xl:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {meeting.scheduleDate}
                                </td>

                                <td className="px-2 2xl:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  {(meeting.publishStatus === "published" ||
                                    meeting.publishStatus === "completed") && (
                                    <div className="inline-flex space-x-2 xl:space-x-6">
                                      <div className="cursor-pointer">
                                        {meeting.noticePdfUrl && (
                                          <a
                                            href={meeting.noticePdfUrl}
                                            className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            Notice
                                          </a>
                                        )}
                                      </div>

                                      {platform?.allowSupplemental &&
                                        meeting.attachments?.length > 0 && (
                                          <Tippy
                                            content="Supplemental Materials"
                                            placement="top"
                                            animation="scale"
                                            delay={[500, 0]}
                                          >
                                            <button
                                              onClick={() =>
                                                setSupplemental({
                                                  visible: true,
                                                  meeting: meeting,
                                                })
                                              }
                                              className="cursor-pointer"
                                              aria-label="Supplemental Materials"
                                            >
                                              <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                            </button>
                                          </Tippy>
                                        )}
                                      {meeting.videoUrl && (
                                        <>
                                          {meeting.pdfOnly ? (
                                            <a
                                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                            >
                                              Watch
                                            </a>
                                          ) : (
                                            <a
                                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                            >
                                              Watch
                                            </a>
                                          )}
                                        </>
                                      )}
                                      {meeting.hasLiveFeed && (
                                        <>
                                          {meeting.pdfOnly ? (
                                            <a
                                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/live/${client}/${meeting.id}`}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                              aria-label="Watch Live Stream"
                                            >
                                              Watch
                                            </a>
                                          ) : (
                                            <a
                                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/live-player/${client}/${meeting.id}`}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                              aria-label="Watch Live Stream"
                                            >
                                              Watch
                                            </a>
                                          )}
                                        </>
                                      )}
                                      {meeting.pdfOnly ? (
                                        <Link
                                          href="/engage/[client]/agenda-pdf/[agenda]"
                                          as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                        >
                                          <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                            Agenda
                                          </a>
                                        </Link>
                                      ) : (
                                        <Link
                                          href="/engage/[client]/[agenda]"
                                          as={`/engage/${client}/${meeting.id}`}
                                        >
                                          <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                            Agenda
                                          </a>
                                        </Link>
                                      )}
                                    </div>
                                  )}
                                  {meeting.publishStatus === "draftreview" &&
                                    client === "broomfield" && (
                                      <div className="flex items-center space-x-2 xl:space-x-6 justify-between">
                                        <div className="cursor-pointer">
                                          {meeting.noticePdfUrl && (
                                            <a
                                              href={meeting.noticePdfUrl}
                                              className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                              target="_blank"
                                              rel="noreferrer"
                                            >
                                              Notice
                                            </a>
                                          )}
                                        </div>
                                        {meeting.pdfOnly ? (
                                          <Link
                                            href="/engage/[client]/agenda-pdf/[agenda]"
                                            as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                          >
                                            <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                              Draft Available
                                            </a>
                                          </Link>
                                        ) : (
                                          <Link
                                            href="/engage/[client]/[agenda]"
                                            as={`/engage/${client}/${meeting.id}`}
                                          >
                                            <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                              Draft Available
                                            </a>
                                          </Link>
                                        )}
                                      </div>
                                    )}
                                  {meeting.publishStatus === "minutes" && (
                                    <div className="inline-flex space-x-2 xl:space-x-6">
                                      <div className="cursor-pointer">
                                        {meeting.noticePdfUrl && (
                                          <a
                                            href={meeting.noticePdfUrl}
                                            className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            Notice
                                          </a>
                                        )}
                                      </div>

                                      {platform?.allowSupplemental &&
                                        meeting.attachments?.length > 0 && (
                                          <Tippy
                                            content="Supplemental Materials"
                                            placement="top"
                                            animation="scale"
                                            delay={[500, 0]}
                                          >
                                            <button
                                              onClick={() =>
                                                setSupplemental({
                                                  visible: true,
                                                  meeting: meeting,
                                                })
                                              }
                                              className="cursor-pointer"
                                              aria-label="Supplemental Materials"
                                            >
                                              <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                            </button>
                                          </Tippy>
                                        )}
                                      {meeting.videoUrl && (
                                        <>
                                          {meeting.pdfOnly ? (
                                            <a
                                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                              aria-label="Watch Meeting Recording"
                                            >
                                              Watch
                                            </a>
                                          ) : (
                                            <a
                                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                              aria-label="Watch Meeting Recording"
                                            >
                                              Watch
                                            </a>
                                          )}
                                        </>
                                      )}
                                      {meeting.pdfOnly ? (
                                        <Link
                                          href="/engage/[client]/agenda-pdf/[agenda]"
                                          as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                        >
                                          <a
                                            className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                            aria-label="Open Agenda PDF"
                                          >
                                            Agenda
                                          </a>
                                        </Link>
                                      ) : (
                                        <Link
                                          href="/engage/[client]/[agenda]"
                                          as={`/engage/${client}/${meeting.id}`}
                                        >
                                          <a
                                            className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                            aria-label="Open Digital Agenda"
                                          >
                                            Agenda
                                          </a>
                                        </Link>
                                      )}
                                      {meeting.minutesPdfOnly ? (
                                        <Link
                                          href="/engage/[client]/minutes-pdf/[agenda]"
                                          as={`/engage/${client}/minutes-pdf/${meeting.id}`}
                                        >
                                          <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                            Minutes
                                          </a>
                                        </Link>
                                      ) : (
                                        <Link
                                          href="/engage/[client]/minutes/[minutes]"
                                          as={`/engage/${client}/minutes/${meeting.id}`}
                                        >
                                          <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                            Minutes
                                          </a>
                                        </Link>
                                      )}
                                    </div>
                                  )}
                                  {meeting.publishStatus ===
                                    "manualminutes" && (
                                    <div className="inline-flex space-x-2 xl:space-x-6">
                                      <div className="cursor-pointer">
                                        {meeting.noticePdfUrl && (
                                          <a
                                            href={meeting.noticePdfUrl}
                                            className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            Notice
                                          </a>
                                        )}
                                      </div>

                                      {platform?.allowSupplemental &&
                                        meeting.attachments?.length > 0 && (
                                          <Tippy
                                            content="Supplemental Materials"
                                            placement="top"
                                            animation="scale"
                                            delay={[500, 0]}
                                          >
                                            <button
                                              onClick={() =>
                                                setSupplemental({
                                                  visible: true,
                                                  meeting: meeting,
                                                })
                                              }
                                              className="cursor-pointer"
                                              aria-label="Supplemental Materials"
                                            >
                                              <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                            </button>
                                          </Tippy>
                                        )}
                                      {meeting.videoUrl && (
                                        <>
                                          {meeting.pdfOnly ? (
                                            <a
                                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                              aria-label="Watch Meeting Recording"
                                            >
                                              Watch
                                            </a>
                                          ) : (
                                            <a
                                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                              aria-label="Watch Meeting Recording"
                                            >
                                              Watch
                                            </a>
                                          )}
                                        </>
                                      )}
                                      {meeting.pdfOnly ? (
                                        <Link
                                          href="/engage/[client]/agenda-pdf/[agenda]"
                                          as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                        >
                                          <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                            Agenda
                                          </a>
                                        </Link>
                                      ) : (
                                        <Link
                                          href="/engage/[client]/[agenda]"
                                          as={`/engage/${client}/${meeting.id}`}
                                        >
                                          <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                            Agenda
                                          </a>
                                        </Link>
                                      )}
                                      {meeting.minutesPdfOnly ? (
                                        <Link
                                          href="/engage/[client]/minutes-pdf/[agenda]"
                                          as={`/engage/${client}/minutes-pdf/${meeting.id}`}
                                        >
                                          <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                            Minutes
                                          </a>
                                        </Link>
                                      ) : (
                                        <Link
                                          href="/engage/[client]/minutes/[minutes]"
                                          as={`/engage/${client}/minutes/${meeting.id}`}
                                        >
                                          <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                            Minutes
                                          </a>
                                        </Link>
                                      )}
                                    </div>
                                  )}
                                  {meeting.publishStatus === "review" && (
                                    <div className="inline-flex space-x-2 xl:space-x-6">
                                      <div className="cursor-pointer">
                                        {meeting.noticePdfUrl && (
                                          <a
                                            href={meeting.noticePdfUrl}
                                            className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            Notice
                                          </a>
                                        )}
                                      </div>

                                      {platform?.allowSupplemental &&
                                        meeting.attachments?.length > 0 && (
                                          <Tippy
                                            content="Supplemental Materials"
                                            placement="top"
                                            animation="scale"
                                            delay={[500, 0]}
                                          >
                                            <button
                                              onClick={() =>
                                                setSupplemental({
                                                  visible: true,
                                                  meeting: meeting,
                                                })
                                              }
                                              className="cursor-pointer"
                                              aria-label="Supplemental Materials"
                                            >
                                              <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                            </button>
                                          </Tippy>
                                        )}
                                      {meeting.videoUrl && (
                                        <>
                                          {meeting.pdfOnly ? (
                                            <a
                                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                              aria-label="Watch Meeting Recording"
                                            >
                                              Watch
                                            </a>
                                          ) : (
                                            <a
                                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                              aria-label="Watch Meeting Recording"
                                            >
                                              Watch
                                            </a>
                                          )}
                                        </>
                                      )}
                                      {meeting.pdfOnly ? (
                                        <Link
                                          href="/engage/[client]/agenda-pdf/[agenda]"
                                          as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                        >
                                          <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                            Agenda
                                          </a>
                                        </Link>
                                      ) : (
                                        <Link
                                          href="/engage/[client]/[agenda]"
                                          as={`/engage/${client}/${meeting.id}`}
                                        >
                                          <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                            Agenda
                                          </a>
                                        </Link>
                                      )}
                                    </div>
                                  )}
                                  {(meeting.publishStatus === "draft" ||
                                    meeting.publishStatus === "draftreview") &&
                                    client !== "broomfield" && (
                                      <div className="inline-flex space-x-2 xl:space-x-6">
                                        {meeting.noticePdfUrl && (
                                          <div className="cursor-pointer">
                                            <a
                                              href={meeting.noticePdfUrl}
                                              className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                              target="_blank"
                                              rel="noreferrer"
                                            >
                                              Notice
                                            </a>
                                          </div>
                                        )}
                                        <div className="text-black select-none">
                                          Agenda Pending
                                        </div>
                                      </div>
                                    )}
                                  {(meeting.publishStatus === "canceled" ||
                                    meeting.publishStatus === "adjourned") && (
                                    <div className="inline-flex space-x-2 xl:space-x-6">
                                      <div className="cursor-pointer">
                                        {meeting.noticePdfUrl && (
                                          <a
                                            href={meeting.noticePdfUrl}
                                            className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            Notice
                                          </a>
                                        )}
                                      </div>
                                      {platform?.allowSupplemental &&
                                        meeting.attachments?.length > 0 && (
                                          <Tippy
                                            content="Supplemental Materials"
                                            placement="top"
                                            animation="scale"
                                            delay={[500, 0]}
                                          >
                                            <button
                                              onClick={() =>
                                                setSupplemental({
                                                  visible: true,
                                                  meeting: meeting,
                                                })
                                              }
                                              className="cursor-pointer"
                                              aria-label="Supplemental Materials"
                                            >
                                              <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                            </button>
                                          </Tippy>
                                        )}
                                      {meeting.pdfOnly ? (
                                        <Link
                                          href="/engage/[client]/agenda-pdf/[agenda]"
                                          as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                        >
                                          <a className="text-red-600 hover:text-indigo-900 underline cursor-pointer">
                                            Meeting{" "}
                                            {meeting.publishStatus ===
                                            "canceled"
                                              ? "Canceled"
                                              : "Adjourned"}
                                          </a>
                                        </Link>
                                      ) : (
                                        <Link
                                          href="/engage/[client]/cancelled/[id]"
                                          as={`/engage/${client}/cancelled/${meeting.id}`}
                                        >
                                          <a className="text-red-600 hover:text-indigo-900 cursor-pointer select-none">
                                            Meeting{" "}
                                            {meeting.publishStatus ===
                                            "canceled"
                                              ? "Canceled"
                                              : "Adjourned"}
                                          </a>
                                        </Link>
                                      )}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          } else {
                            return null;
                          }
                        } else {
                          return (
                            <tr
                              key={meeting.id}
                              className={
                                personIdx % 2 === 0 ? "bg-white" : "bg-gray-100"
                              }
                            >
                              <td className="px-2 2xl:px-6 py-4 text-sm font-medium text-gray-900">
                                <div>{meeting.meetingType}</div>
                                <div>{meeting.room.roomName}</div>
                              </td>
                              <td className="px-2 2xl:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {meeting.scheduleDate}
                              </td>

                              <td className="px-2 2xl:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                {(meeting.publishStatus === "published" ||
                                  meeting.publishStatus === "completed") && (
                                  <div className="inline-flex space-x-2 xl:space-x-6">
                                    <div className="cursor-pointer">
                                      {meeting.noticePdfUrl && (
                                        <a
                                          href={meeting.noticePdfUrl}
                                          className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          Notice
                                        </a>
                                      )}
                                    </div>

                                    {platform?.allowSupplemental &&
                                      meeting.attachments?.length > 0 && (
                                        <Tippy
                                          content="Supplemental Materials"
                                          placement="top"
                                          animation="scale"
                                          delay={[500, 0]}
                                        >
                                          <button
                                            onClick={() =>
                                              setSupplemental({
                                                visible: true,
                                                meeting: meeting,
                                              })
                                            }
                                            className="cursor-pointer"
                                            aria-label="Supplemental Materials"
                                          >
                                            <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                          </button>
                                        </Tippy>
                                      )}
                                    {meeting.videoUrl && (
                                      <>
                                        {meeting.pdfOnly ? (
                                          <a
                                            href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                            aria-label="Watch Meeting Recording"
                                          >
                                            Watch
                                          </a>
                                        ) : (
                                          <a
                                            href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                            aria-label="Watch Meeting Recording"
                                          >
                                            Watch
                                          </a>
                                        )}
                                      </>
                                    )}
                                    {meeting.hasLiveFeed && (
                                      <>
                                        {meeting.pdfOnly ? (
                                          <a
                                            href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/live/${client}/${meeting.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                            aria-label="Watch Meeting"
                                          >
                                            Watch
                                          </a>
                                        ) : (
                                          <a
                                            href={`${process.env.NEXT_PUBLIC_BASE_URL}/live-player/${client}/${meeting.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                            aria-label="Watch Meeting"
                                          >
                                            Watch
                                          </a>
                                        )}
                                      </>
                                    )}
                                    {meeting.pdfOnly ? (
                                      <Link
                                        href="/engage/[client]/agenda-pdf/[agenda]"
                                        as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                      >
                                        <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                          Agenda
                                        </a>
                                      </Link>
                                    ) : (
                                      <Link
                                        href="/engage/[client]/[agenda]"
                                        as={`/engage/${client}/${meeting.id}`}
                                      >
                                        <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                          Agenda
                                        </a>
                                      </Link>
                                    )}
                                  </div>
                                )}
                                {meeting.publishStatus === "draftreview" &&
                                  client === "broomfield" && (
                                    <div className="flex items-center space-x-2 xl:space-x-6 justify-between">
                                      <div className="cursor-pointer">
                                        {meeting.noticePdfUrl && (
                                          <a
                                            href={meeting.noticePdfUrl}
                                            className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            Notice
                                          </a>
                                        )}
                                      </div>
                                      <Link
                                        href="/engage/[client]/[agenda]"
                                        as={`/engage/${client}/${meeting.id}`}
                                      >
                                        <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                          Draft Available
                                        </a>
                                      </Link>
                                    </div>
                                  )}
                                {meeting.publishStatus === "minutes" && (
                                  <div className="inline-flex space-x-2 xl:space-x-6">
                                    <div className="cursor-pointer">
                                      {meeting.noticePdfUrl && (
                                        <a
                                          href={meeting.noticePdfUrl}
                                          className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          Notice
                                        </a>
                                      )}
                                    </div>

                                    {platform?.allowSupplemental &&
                                      meeting.attachments?.length > 0 && (
                                        <Tippy
                                          content="Supplemental Materials"
                                          placement="top"
                                          animation="scale"
                                          delay={[500, 0]}
                                        >
                                          <button
                                            onClick={() =>
                                              setSupplemental({
                                                visible: true,
                                                meeting: meeting,
                                              })
                                            }
                                            className="cursor-pointer"
                                            aria-label="Supplemental Materials"
                                          >
                                            <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                          </button>
                                        </Tippy>
                                      )}
                                    {meeting.videoUrl && (
                                      <>
                                        {meeting.pdfOnly ? (
                                          <a
                                            href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                            aria-label="Watch Meeting Recording"
                                          >
                                            Watch
                                          </a>
                                        ) : (
                                          <a
                                            href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                            aria-label="Watch Meeting Recording"
                                          >
                                            Watch
                                          </a>
                                        )}
                                      </>
                                    )}
                                    {meeting.pdfOnly ? (
                                      <Link
                                        href="/engage/[client]/agenda-pdf/[agenda]"
                                        as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                      >
                                        <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                          Agenda
                                        </a>
                                      </Link>
                                    ) : (
                                      <Link
                                        href="/engage/[client]/[agenda]"
                                        as={`/engage/${client}/${meeting.id}`}
                                      >
                                        <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                          Agenda
                                        </a>
                                      </Link>
                                    )}
                                    {meeting.minutesPdfOnly ? (
                                      <Link
                                        href="/engage/[client]/minutes-pdf/[agenda]"
                                        as={`/engage/${client}/minutes-pdf/${meeting.id}`}
                                      >
                                        <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                          Minutes
                                        </a>
                                      </Link>
                                    ) : (
                                      <Link
                                        href="/engage/[client]/minutes/[minutes]"
                                        as={`/engage/${client}/minutes/${meeting.id}`}
                                      >
                                        <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                          Minutes
                                        </a>
                                      </Link>
                                    )}
                                  </div>
                                )}
                                {meeting.publishStatus === "manualminutes" && (
                                  <div className="inline-flex space-x-2 xl:space-x-6">
                                    <div className="cursor-pointer">
                                      {meeting.noticePdfUrl && (
                                        <a
                                          href={meeting.noticePdfUrl}
                                          className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          Notice
                                        </a>
                                      )}
                                    </div>

                                    {platform?.allowSupplemental &&
                                      meeting.attachments?.length > 0 && (
                                        <Tippy
                                          content="Supplemental Materials"
                                          placement="top"
                                          animation="scale"
                                          delay={[500, 0]}
                                        >
                                          <button
                                            onClick={() =>
                                              setSupplemental({
                                                visible: true,
                                                meeting: meeting,
                                              })
                                            }
                                            className="cursor-pointer"
                                            aria-label="Supplemental Materials"
                                          >
                                            <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                          </button>
                                        </Tippy>
                                      )}
                                    {meeting.videoUrl && (
                                      <>
                                        {meeting.pdfOnly ? (
                                          <a
                                            href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                            aria-label="Watch Meeting Recording"
                                          >
                                            Watch
                                          </a>
                                        ) : (
                                          <a
                                            href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                            aria-label="Watch Meeting Recording"
                                          >
                                            Watch
                                          </a>
                                        )}
                                      </>
                                    )}
                                    {meeting.pdfOnly ? (
                                      <Link
                                        href="/engage/[client]/agenda-pdf/[agenda]"
                                        as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                      >
                                        <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                          Agenda
                                        </a>
                                      </Link>
                                    ) : (
                                      <Link
                                        href="/engage/[client]/[agenda]"
                                        as={`/engage/${client}/${meeting.id}`}
                                      >
                                        <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                          Agenda
                                        </a>
                                      </Link>
                                    )}
                                    {meeting.minutesPdfOnly ? (
                                      <Link
                                        href="/engage/[client]/minutes-pdf/[agenda]"
                                        as={`/engage/${client}/minutes-pdf/${meeting.id}`}
                                      >
                                        <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                          Minutes
                                        </a>
                                      </Link>
                                    ) : (
                                      <Link
                                        href="/engage/[client]/minutes/[minutes]"
                                        as={`/engage/${client}/minutes/${meeting.id}`}
                                      >
                                        <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                          Minutes
                                        </a>
                                      </Link>
                                    )}
                                  </div>
                                )}
                                {meeting.publishStatus === "review" && (
                                  <div className="inline-flex space-x-2 xl:space-x-6">
                                    <div className="cursor-pointer">
                                      {meeting.noticePdfUrl && (
                                        <a
                                          href={meeting.noticePdfUrl}
                                          className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          Notice
                                        </a>
                                      )}
                                    </div>

                                    {platform?.allowSupplemental &&
                                      meeting.attachments?.length > 0 && (
                                        <Tippy
                                          content="Supplemental Materials"
                                          placement="top"
                                          animation="scale"
                                          delay={[500, 0]}
                                        >
                                          <button
                                            onClick={() =>
                                              setSupplemental({
                                                visible: true,
                                                meeting: meeting,
                                              })
                                            }
                                            className="cursor-pointer"
                                            aria-label="Supplemental Materials"
                                          >
                                            <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                          </button>
                                        </Tippy>
                                      )}
                                    {meeting.videoUrl && (
                                      <>
                                        {meeting.pdfOnly ? (
                                          <a
                                            href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                            aria-label="Watch Meeting Recording"
                                          >
                                            Watch
                                          </a>
                                        ) : (
                                          <a
                                            href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                            aria-label="Watch Meeting Recording"
                                          >
                                            Watch
                                          </a>
                                        )}
                                      </>
                                    )}
                                    {meeting.pdfOnly ? (
                                      <Link
                                        href="/engage/[client]/agenda-pdf/[agenda]"
                                        as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                      >
                                        <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                          Agenda
                                        </a>
                                      </Link>
                                    ) : (
                                      <Link
                                        href="/engage/[client]/[agenda]"
                                        as={`/engage/${client}/${meeting.id}`}
                                      >
                                        <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                          Agenda
                                        </a>
                                      </Link>
                                    )}
                                  </div>
                                )}
                                {(meeting.publishStatus === "draft" ||
                                  meeting.publishStatus === "draftreview") &&
                                  client !== "broomfield" && (
                                    <div className="flex items-center">
                                      {meeting.noticePdfUrl && (
                                        <div className="cursor-pointer">
                                          <a
                                            href={meeting.noticePdfUrl}
                                            className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            Notice
                                          </a>
                                        </div>
                                      )}
                                      <div className="text-black select-none">
                                        Agenda Pending
                                      </div>
                                    </div>
                                  )}
                                {(meeting.publishStatus === "canceled" ||
                                  meeting.publishStatus === "adjourned") && (
                                  <div className="inline-flex space-x-2 xl:space-x-6">
                                    <div className="cursor-pointer">
                                      {meeting.noticePdfUrl && (
                                        <a
                                          href={meeting.noticePdfUrl}
                                          className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          Notice
                                        </a>
                                      )}
                                    </div>
                                    {platform?.allowSupplemental &&
                                      meeting.attachments?.length > 0 && (
                                        <Tippy
                                          content="Supplemental Materials"
                                          placement="top"
                                          animation="scale"
                                          delay={[500, 0]}
                                        >
                                          <button
                                            onClick={() =>
                                              setSupplemental({
                                                visible: true,
                                                meeting: meeting,
                                              })
                                            }
                                            className="cursor-pointer"
                                            aria-label="Supplemental Materials"
                                          >
                                            <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                          </button>
                                        </Tippy>
                                      )}
                                    {meeting.pdfOnly ? (
                                      <Link
                                        href="/engage/[client]/agenda-pdf/[agenda]"
                                        as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                      >
                                        <a className="text-red-600 hover:text-indigo-900 underline cursor-pointer">
                                          Meeting{" "}
                                          {meeting.publishStatus === "canceled"
                                            ? "Canceled"
                                            : "Adjourned"}
                                        </a>
                                      </Link>
                                    ) : (
                                      <Link
                                        href="/engage/[client]/cancelled/[id]"
                                        as={`/engage/${client}/cancelled/${meeting.id}`}
                                      >
                                        <a className="text-red-600 hover:text-indigo-900 cursor-pointer select-none">
                                          Meeting{" "}
                                          {meeting.publishStatus === "canceled"
                                            ? "Canceled"
                                            : "Adjourned"}
                                        </a>
                                      </Link>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        }
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          {/* Embed Data */}
          <div className="hidden sm:block xl:hidden overflow-hidden">
            <div>
              {upcomingList?.map((meeting, uIdx) => {
                if (board) {
                  if (board === meeting.department._id) {
                    return (
                      <div key={meeting.id}>
                        {(meeting.publishStatus === "published" ||
                          meeting.publishStatus === "completed") && (
                          <div className="">
                            <div
                              className={clsx(
                                uIdx % 2 !== 0
                                  ? "bg-gray-50/50 border-gray-300"
                                  : "bg-gray-100 border-gray-200",
                                "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200",
                              )}
                            >
                              <div className="col-span-2">
                                <p className="text-sm font-medium text-black">
                                  {meeting.meetingType}
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.room.roomName}
                                  </span>
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.scheduleDate}
                                  </span>
                                </p>
                                {platform?.showDuration && (
                                  <p className="mt-1 flex items-center text-sm text-gray-900">
                                    <span className="truncate">
                                      {meeting.duration}
                                    </span>
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center space-x-2 justify-end">
                                <div className="cursor-pointer">
                                  {meeting.noticePdfUrl && (
                                    <a
                                      href={meeting.noticePdfUrl}
                                      className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Notice
                                    </a>
                                  )}
                                </div>

                                {platform?.allowSupplemental &&
                                  meeting.attachments?.length > 0 && (
                                    <Tippy
                                      content="Supplemental Materials"
                                      placement="top"
                                      animation="scale"
                                      delay={[500, 0]}
                                    >
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSupplemental({
                                            visible: true,
                                            meeting: meeting,
                                          });
                                        }}
                                        className="cursor-pointer"
                                        aria-label="Supplemental Materials"
                                      >
                                        <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                      </button>
                                    </Tippy>
                                  )}
                                {meeting.videoUrl && (
                                  <>
                                    {meeting.pdfOnly ? (
                                      <a
                                        href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                      >
                                        Watch
                                      </a>
                                    ) : (
                                      <a
                                        href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                      >
                                        Watch
                                      </a>
                                    )}
                                  </>
                                )}
                                {meeting.hasLiveFeed && (
                                  <>
                                    {meeting.pdfOnly ? (
                                      <a
                                        href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/live/${client}/${meeting.id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                      >
                                        Watch
                                      </a>
                                    ) : (
                                      <a
                                        href={`${process.env.NEXT_PUBLIC_BASE_URL}/live-player/${client}/${meeting.id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                      >
                                        Watch
                                      </a>
                                    )}
                                  </>
                                )}
                                {meeting.pdfOnly ? (
                                  <Link
                                    href="/engage/[client]/agenda-pdf/[agenda]"
                                    as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                      Agenda
                                    </a>
                                  </Link>
                                ) : (
                                  <Link
                                    href="/engage/[client]/[agenda]"
                                    as={`/engage/${client}/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                      Agenda
                                    </a>
                                  </Link>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        {meeting.publishStatus === "minutes" && (
                          <div className="">
                            <div
                              className={clsx(
                                uIdx % 2 !== 0
                                  ? "bg-gray-50/50 border-gray-300"
                                  : "bg-gray-100 border-gray-200",
                                "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200",
                              )}
                            >
                              <div className="col-span-2">
                                <p className="text-sm font-medium text-black">
                                  {meeting.meetingType}
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.room.roomName}
                                  </span>
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.scheduleDate}
                                  </span>
                                </p>
                              </div>
                              <div className="flex items-center justify-end mt-0.5 space-x-2">
                                <div className="cursor-pointer">
                                  {meeting.noticePdfUrl && (
                                    <a
                                      href={meeting.noticePdfUrl}
                                      className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Notice
                                    </a>
                                  )}
                                </div>
                                {platform?.allowSupplemental &&
                                  meeting.attachments?.length > 0 && (
                                    <Tippy
                                      content="Supplemental Materials"
                                      placement="top"
                                      animation="scale"
                                      delay={[500, 0]}
                                    >
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSupplemental({
                                            visible: true,
                                            meeting: meeting,
                                          });
                                        }}
                                        className="cursor-pointer"
                                        aria-label="Supplemental Materials"
                                      >
                                        <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                      </button>
                                    </Tippy>
                                  )}

                                {meeting.videoUrl && (
                                  <>
                                    {meeting.pdfOnly ? (
                                      <a
                                        href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                      >
                                        Watch
                                      </a>
                                    ) : (
                                      <a
                                        href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                      >
                                        Watch
                                      </a>
                                    )}
                                  </>
                                )}
                                {meeting.pdfOnly ? (
                                  <Link
                                    href="/engage/[client]/agenda-pdf/[agenda]"
                                    as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                      Agenda
                                    </a>
                                  </Link>
                                ) : (
                                  <Link
                                    href="/engage/[client]/[agenda]"
                                    as={`/engage/${client}/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                      Agenda
                                    </a>
                                  </Link>
                                )}
                                {meeting.minutesPdfOnly ? (
                                  <Link
                                    href="/engage/[client]/minutes-pdf/[agenda]"
                                    as={`/engage/${client}/minutes-pdf/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                      Minutes
                                    </a>
                                  </Link>
                                ) : (
                                  <Link
                                    href="/engage/[client]/minutes/[minutes]"
                                    as={`/engage/${client}/minutes/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                      Minutes
                                    </a>
                                  </Link>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        {meeting.publishStatus === "manualminutes" && (
                          <div className="">
                            <div
                              className={clsx(
                                uIdx % 2 !== 0
                                  ? "bg-gray-50/50 border-gray-300"
                                  : "bg-gray-100 border-gray-200",
                                "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200",
                              )}
                            >
                              <div className="col-span-2">
                                <p className="text-sm font-medium text-black">
                                  {meeting.meetingType}
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.room.roomName}
                                  </span>
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.scheduleDate}
                                  </span>
                                </p>
                              </div>
                              <div className="flex items-center justify-end mt-0.5 space-x-2">
                                <div className="cursor-pointer">
                                  {meeting.noticePdfUrl && (
                                    <a
                                      href={meeting.noticePdfUrl}
                                      className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Notice
                                    </a>
                                  )}
                                </div>
                                {platform?.allowSupplemental &&
                                  meeting.attachments?.length > 0 && (
                                    <Tippy
                                      content="Supplemental Materials"
                                      placement="top"
                                      animation="scale"
                                      delay={[500, 0]}
                                    >
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSupplemental({
                                            visible: true,
                                            meeting: meeting,
                                          });
                                        }}
                                        className="cursor-pointer"
                                        aria-label="Supplemental Materials"
                                      >
                                        <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                      </button>
                                    </Tippy>
                                  )}

                                {meeting.videoUrl && (
                                  <>
                                    {meeting.pdfOnly ? (
                                      <a
                                        href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                      >
                                        Watch
                                      </a>
                                    ) : (
                                      <a
                                        href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                      >
                                        Watch
                                      </a>
                                    )}
                                  </>
                                )}
                                {meeting.pdfOnly ? (
                                  <Link
                                    href="/engage/[client]/agenda-pdf/[agenda]"
                                    as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                      Agenda
                                    </a>
                                  </Link>
                                ) : (
                                  <Link
                                    href="/engage/[client]/[agenda]"
                                    as={`/engage/${client}/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                      Agenda
                                    </a>
                                  </Link>
                                )}
                                {meeting.minutesPdfOnly ? (
                                  <Link
                                    href="/engage/[client]/minutes-pdf/[agenda]"
                                    as={`/engage/${client}/minutes-pdf/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                      Minutes
                                    </a>
                                  </Link>
                                ) : (
                                  <Link
                                    href="/engage/[client]/minutes/[minutes]"
                                    as={`/engage/${client}/minutes/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                      Minutes
                                    </a>
                                  </Link>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        {(meeting.publishStatus === "draft" ||
                          meeting.publishStatus === "draftreview") &&
                          client !== "broomfield" && (
                            <div
                              className={clsx(
                                uIdx % 2 !== 0
                                  ? "bg-gray-50/50 border-gray-300"
                                  : "bg-gray-100 border-gray-200",
                                "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm transition duration-200",
                              )}
                            >
                              <div className="col-span-2">
                                <p className="text-sm font-medium text-black">
                                  {meeting.meetingType}
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.room.roomName}
                                  </span>
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.scheduleDate}
                                  </span>
                                </p>
                              </div>
                              <div className="flex items-center space-x-2 justify-end">
                                <div className="cursor-pointer">
                                  {meeting.noticePdfUrl && (
                                    <a
                                      href={meeting.noticePdfUrl}
                                      className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Notice
                                    </a>
                                  )}
                                </div>
                                <p className="mt-1 text-sm text-black text-right">
                                  Agenda Pending
                                </p>
                              </div>
                            </div>
                          )}
                        {meeting.publishStatus === "draftreview" &&
                          client === "broomfield" && (
                            <div className="">
                              <div
                                className={clsx(
                                  uIdx % 2 !== 0
                                    ? "bg-gray-50/50 border-gray-300"
                                    : "bg-gray-100 border-gray-200",
                                  "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200",
                                )}
                              >
                                <div className="col-span-2">
                                  <p className="text-sm font-medium text-black">
                                    {meeting.meetingType}
                                  </p>
                                  <p className="mt-1 flex items-center text-sm text-gray-900">
                                    <span className="truncate">
                                      {meeting.room.roomName}
                                    </span>
                                  </p>
                                  <p className="mt-1 flex items-center text-sm text-gray-900">
                                    <span className="truncate">
                                      {meeting.scheduleDate}
                                    </span>
                                  </p>
                                  {platform?.showDuration && (
                                    <p className="mt-1 flex items-center text-sm text-gray-900">
                                      <span className="truncate">
                                        {meeting.duration}
                                      </span>
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center space-x-2 justify-end">
                                  <div className="cursor-pointer">
                                    {meeting.noticePdfUrl && (
                                      <a
                                        href={meeting.noticePdfUrl}
                                        className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        Notice
                                      </a>
                                    )}
                                  </div>
                                  <Link
                                    href="/engage/[id]/[agenda]"
                                    as={`/engage/${client}/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                      Draft Available
                                    </a>
                                  </Link>
                                </div>
                              </div>
                            </div>
                          )}
                        {meeting.publishStatus === "review" && (
                          <div>
                            <div
                              className={clsx(
                                uIdx % 2 !== 0
                                  ? "bg-gray-50/50 border-gray-300"
                                  : "bg-gray-100 border-gray-200",
                                "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200",
                              )}
                            >
                              <div className="col-span-2">
                                <p className="text-sm font-medium text-black">
                                  {meeting.meetingType}
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.room.roomName}
                                  </span>
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.scheduleDate}
                                  </span>
                                </p>
                              </div>
                              <div className="flex items-center justify-end mt-0.5 space-x-2">
                                <div className="cursor-pointer">
                                  {meeting.noticePdfUrl && (
                                    <a
                                      href={meeting.noticePdfUrl}
                                      className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Notice
                                    </a>
                                  )}
                                </div>
                                {platform?.allowSupplemental &&
                                  meeting.attachments?.length > 0 && (
                                    <Tippy
                                      content="Supplemental Materials"
                                      placement="top"
                                      animation="scale"
                                      delay={[500, 0]}
                                    >
                                      <button
                                        onClick={() =>
                                          setSupplemental({
                                            visible: true,
                                            meeting: meeting,
                                          })
                                        }
                                        className="cursor-pointer"
                                        aria-label="Supplemental Materials"
                                      >
                                        <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                      </button>
                                    </Tippy>
                                  )}

                                {meeting.videoUrl && (
                                  <>
                                    {meeting.pdfOnly ? (
                                      <a
                                        href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                      >
                                        Watch
                                      </a>
                                    ) : (
                                      <a
                                        href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                      >
                                        Watch
                                      </a>
                                    )}
                                  </>
                                )}
                                {meeting.pdfOnly ? (
                                  <Link
                                    href="/engage/[client]/agenda-pdf/[agenda]"
                                    as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                      Agenda
                                    </a>
                                  </Link>
                                ) : (
                                  <Link
                                    href="/engage/[client]/[agenda]"
                                    as={`/engage/${client}/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                      Agenda
                                    </a>
                                  </Link>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        {(meeting.publishStatus === "canceled" ||
                          meeting.publishStatus === "adjourned") && (
                          <div>
                            <div
                              className={clsx(
                                uIdx % 2 !== 0
                                  ? "bg-gray-50/50 border-gray-300"
                                  : "bg-gray-100 border-gray-200",
                                "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200",
                              )}
                            >
                              <div className="col-span-2">
                                <p className="text-sm font-medium text-black">
                                  {meeting.meetingType}
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.room.roomName}
                                  </span>
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.scheduleDate}
                                  </span>
                                </p>
                              </div>
                              <div className="flex items-center justify-end mt-0.5 space-x-2">
                                <div className="cursor-pointer">
                                  {meeting.noticePdfUrl && (
                                    <a
                                      href={meeting.noticePdfUrl}
                                      className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Notice
                                    </a>
                                  )}
                                </div>
                                {platform?.allowSupplemental &&
                                  meeting.attachments?.length > 0 && (
                                    <Tippy
                                      content="Supplemental Materials"
                                      placement="top"
                                      animation="scale"
                                      delay={[500, 0]}
                                    >
                                      <button
                                        onClick={() =>
                                          setSupplemental({
                                            visible: true,
                                            meeting: meeting,
                                          })
                                        }
                                        className="cursor-pointer"
                                        aria-label="Supplemental Materials"
                                      >
                                        <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                      </button>
                                    </Tippy>
                                  )}
                                {meeting.pdfOnly ? (
                                  <Link
                                    href="/engage/[client]/agenda-pdf/[agenda]"
                                    as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                  >
                                    <a className="text-red-600 hover:text-indigo-900 underline cursor-pointer">
                                      Meeting{" "}
                                      {meeting.publishStatus === "canceled"
                                        ? "Canceled"
                                        : "Adjourned"}
                                    </a>
                                  </Link>
                                ) : (
                                  <Link
                                    href="/engage/[client]/cancelled/[id]"
                                    as={`/engage/${client}/cancelled/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-red-600 hover:text-indigo-900 cursor-pointer select-none">
                                      Meeting{" "}
                                      {meeting.publishStatus === "canceled"
                                        ? "Canceled"
                                        : "Adjourned"}
                                    </a>
                                  </Link>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  } else {
                    return null;
                  }
                } else {
                  return (
                    <div key={meeting.id}>
                      {(meeting.publishStatus === "published" ||
                        meeting.publishStatus === "completed") && (
                        <div className="">
                          <div
                            className={clsx(
                              uIdx % 2 !== 0
                                ? "bg-gray-50/50 border-gray-300"
                                : "bg-gray-100 border-gray-200",
                              "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200",
                            )}
                          >
                            <div className="col-span-2">
                              <p className="text-sm font-medium text-black">
                                {meeting.meetingType}
                              </p>
                              <p className="mt-1 flex items-center text-sm text-gray-900">
                                <span className="truncate">
                                  {meeting.room.roomName}
                                </span>
                              </p>
                              <p className="mt-1 flex items-center text-sm text-gray-900">
                                <span className="truncate">
                                  {meeting.scheduleDate}
                                </span>
                              </p>
                              {platform?.showDuration && (
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.duration}
                                  </span>
                                </p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2 justify-end">
                              <div className="cursor-pointer">
                                {meeting.noticePdfUrl && (
                                  <a
                                    href={meeting.noticePdfUrl}
                                    className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Notice
                                  </a>
                                )}
                              </div>

                              {platform?.allowSupplemental &&
                                meeting.attachments?.length > 0 && (
                                  <Tippy
                                    content="Supplemental Materials"
                                    placement="top"
                                    animation="scale"
                                    delay={[500, 0]}
                                  >
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSupplemental({
                                          visible: true,
                                          meeting: meeting,
                                        });
                                      }}
                                      className="cursor-pointer"
                                      aria-label="Supplemental Materials"
                                    >
                                      <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                    </button>
                                  </Tippy>
                                )}
                              {meeting.videoUrl && (
                                <>
                                  {meeting.pdfOnly ? (
                                    <a
                                      href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                    >
                                      Watch
                                    </a>
                                  ) : (
                                    <a
                                      href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                    >
                                      Watch
                                    </a>
                                  )}
                                </>
                              )}
                              {meeting.hasLiveFeed && (
                                <>
                                  {meeting.pdfOnly ? (
                                    <a
                                      href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/live/${client}/${meeting.id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                    >
                                      Watch
                                    </a>
                                  ) : (
                                    <a
                                      href={`${process.env.NEXT_PUBLIC_BASE_URL}/live-player/${client}/${meeting.id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                    >
                                      Watch
                                    </a>
                                  )}
                                </>
                              )}
                              {meeting.pdfOnly ? (
                                <Link
                                  href="/engage/[client]/agenda-pdf/[agenda]"
                                  as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                >
                                  <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                    Agenda
                                  </a>
                                </Link>
                              ) : (
                                <Link
                                  href="/engage/[client]/[agenda]"
                                  as={`/engage/${client}/${meeting.id}`}
                                >
                                  <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                    Agenda
                                  </a>
                                </Link>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      {meeting.publishStatus === "minutes" && (
                        <div className="">
                          <div
                            className={clsx(
                              uIdx % 2 !== 0
                                ? "bg-gray-50/50 border-gray-300"
                                : "bg-gray-100 border-gray-200",
                              "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200",
                            )}
                          >
                            <div className="col-span-2">
                              <p className="text-sm font-medium text-black">
                                {meeting.meetingType}
                              </p>
                              <p className="mt-1 flex items-center text-sm text-gray-900">
                                <span className="truncate">
                                  {meeting.room.roomName}
                                </span>
                              </p>
                              <p className="mt-1 flex items-center text-sm text-gray-900">
                                <span className="truncate">
                                  {meeting.scheduleDate}
                                </span>
                              </p>
                            </div>
                            <div className="flex items-center justify-end mt-0.5 space-x-2">
                              <div className="cursor-pointer">
                                {meeting.noticePdfUrl && (
                                  <a
                                    href={meeting.noticePdfUrl}
                                    className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Notice
                                  </a>
                                )}
                              </div>
                              {platform?.allowSupplemental &&
                                meeting.attachments?.length > 0 && (
                                  <Tippy
                                    content="Supplemental Materials"
                                    placement="top"
                                    animation="scale"
                                    delay={[500, 0]}
                                  >
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSupplemental({
                                          visible: true,
                                          meeting: meeting,
                                        });
                                      }}
                                      className="cursor-pointer"
                                      aria-label="Supplemental Materials"
                                    >
                                      <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                    </button>
                                  </Tippy>
                                )}

                              {meeting.videoUrl && (
                                <>
                                  {meeting.pdfOnly ? (
                                    <a
                                      href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                    >
                                      Watch
                                    </a>
                                  ) : (
                                    <a
                                      href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                    >
                                      Watch
                                    </a>
                                  )}
                                </>
                              )}
                              {meeting.pdfOnly ? (
                                <Link
                                  href="/engage/[client]/agenda-pdf/[agenda]"
                                  as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                >
                                  <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                    Agenda
                                  </a>
                                </Link>
                              ) : (
                                <Link
                                  href="/engage/[client]/[agenda]"
                                  as={`/engage/${client}/${meeting.id}`}
                                >
                                  <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                    Agenda
                                  </a>
                                </Link>
                              )}
                              {meeting.minutesPdfOnly ? (
                                <Link
                                  href="/engage/[client]/minutes-pdf/[agenda]"
                                  as={`/engage/${client}/minutes-pdf/${meeting.id}`}
                                >
                                  <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                    Minutes
                                  </a>
                                </Link>
                              ) : (
                                <Link
                                  href="/engage/[client]/minutes/[minutes]"
                                  as={`/engage/${client}/minutes/${meeting.id}`}
                                >
                                  <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                    Minutes
                                  </a>
                                </Link>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      {meeting.publishStatus === "manualminutes" && (
                        <div className="">
                          <div
                            className={clsx(
                              uIdx % 2 !== 0
                                ? "bg-gray-50/50 border-gray-300"
                                : "bg-gray-100 border-gray-200",
                              "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200",
                            )}
                          >
                            <div className="col-span-2">
                              <p className="text-sm font-medium text-black">
                                {meeting.meetingType}
                              </p>
                              <p className="mt-1 flex items-center text-sm text-gray-900">
                                <span className="truncate">
                                  {meeting.room.roomName}
                                </span>
                              </p>
                              <p className="mt-1 flex items-center text-sm text-gray-900">
                                <span className="truncate">
                                  {meeting.scheduleDate}
                                </span>
                              </p>
                            </div>
                            <div className="flex items-center justify-end mt-0.5 space-x-2">
                              <div className="cursor-pointer">
                                {meeting.noticePdfUrl && (
                                  <a
                                    href={meeting.noticePdfUrl}
                                    className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Notice
                                  </a>
                                )}
                              </div>
                              {platform?.allowSupplemental &&
                                meeting.attachments?.length > 0 && (
                                  <Tippy
                                    content="Supplemental Materials"
                                    placement="top"
                                    animation="scale"
                                    delay={[500, 0]}
                                  >
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSupplemental({
                                          visible: true,
                                          meeting: meeting,
                                        });
                                      }}
                                      className="cursor-pointer"
                                      aria-label="Supplemental Materials"
                                    >
                                      <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                    </button>
                                  </Tippy>
                                )}

                              {meeting.videoUrl && (
                                <>
                                  {meeting.pdfOnly ? (
                                    <a
                                      href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                    >
                                      Watch
                                    </a>
                                  ) : (
                                    <a
                                      href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                    >
                                      Watch
                                    </a>
                                  )}
                                </>
                              )}
                              {meeting.pdfOnly ? (
                                <Link
                                  href="/engage/[client]/agenda-pdf/[agenda]"
                                  as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                >
                                  <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                    Agenda
                                  </a>
                                </Link>
                              ) : (
                                <Link
                                  href="/engage/[client]/[agenda]"
                                  as={`/engage/${client}/${meeting.id}`}
                                >
                                  <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                    Agenda
                                  </a>
                                </Link>
                              )}
                              {meeting.minutesPdfOnly ? (
                                <Link
                                  href="/engage/[client]/minutes-pdf/[agenda]"
                                  as={`/engage/${client}/minutes-pdf/${meeting.id}`}
                                >
                                  <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                    Minutes
                                  </a>
                                </Link>
                              ) : (
                                <Link
                                  href="/engage/[client]/minutes/[minutes]"
                                  as={`/engage/${client}/minutes/${meeting.id}`}
                                >
                                  <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                    Minutes
                                  </a>
                                </Link>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      {(meeting.publishStatus === "draft" ||
                        meeting.publishStatus === "draftreview") &&
                        client !== "broomfield" && (
                          <div
                            className={clsx(
                              uIdx % 2 !== 0
                                ? "bg-gray-50/50 border-gray-300"
                                : "bg-gray-100 border-gray-200",
                              "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm transition duration-200",
                            )}
                          >
                            <div className="col-span-2">
                              <p className="text-sm font-medium text-black">
                                {meeting.meetingType}
                              </p>
                              <p className="mt-1 flex items-center text-sm text-gray-900">
                                <span className="truncate">
                                  {meeting.room.roomName}
                                </span>
                              </p>
                              <p className="mt-1 flex items-center text-sm text-gray-900">
                                <span className="truncate">
                                  {meeting.scheduleDate}
                                </span>
                              </p>
                            </div>
                            <div className="flex items-center space-x-2 justify-end">
                              <div className="cursor-pointer">
                                {meeting.noticePdfUrl && (
                                  <a
                                    href={meeting.noticePdfUrl}
                                    className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Notice
                                  </a>
                                )}
                              </div>
                              <p className="mt-1 text-sm text-black text-right">
                                Agenda Pending
                              </p>
                            </div>
                          </div>
                        )}
                      {meeting.publishStatus === "draftreview" &&
                        client === "broomfield" && (
                          <div className="">
                            <div
                              className={clsx(
                                uIdx % 2 !== 0
                                  ? "bg-gray-50/50 border-gray-300"
                                  : "bg-gray-100 border-gray-200",
                                "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200",
                              )}
                            >
                              <div className="col-span-2">
                                <p className="text-sm font-medium text-black">
                                  {meeting.meetingType}
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.room.roomName}
                                  </span>
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.scheduleDate}
                                  </span>
                                </p>
                                {platform?.showDuration && (
                                  <p className="mt-1 flex items-center text-sm text-gray-900">
                                    <span className="truncate">
                                      {meeting.duration}
                                    </span>
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center space-x-2 justify-end">
                                <div className="cursor-pointer">
                                  {meeting.noticePdfUrl && (
                                    <a
                                      href={meeting.noticePdfUrl}
                                      className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Notice
                                    </a>
                                  )}
                                </div>
                                <Link
                                  href="/engage/[id]/[agenda]"
                                  as={`/engage/${client}/${meeting.id}`}
                                >
                                  <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                    Draft Available
                                  </a>
                                </Link>
                              </div>
                            </div>
                          </div>
                        )}
                      {meeting.publishStatus === "review" && (
                        <div>
                          <div
                            className={clsx(
                              uIdx % 2 !== 0
                                ? "bg-gray-50/50 border-gray-300"
                                : "bg-gray-100 border-gray-200",
                              "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200",
                            )}
                          >
                            <div className="col-span-2">
                              <p className="text-sm font-medium text-black">
                                {meeting.meetingType}
                              </p>
                              <p className="mt-1 flex items-center text-sm text-gray-900">
                                <span className="truncate">
                                  {meeting.room.roomName}
                                </span>
                              </p>
                              <p className="mt-1 flex items-center text-sm text-gray-900">
                                <span className="truncate">
                                  {meeting.scheduleDate}
                                </span>
                              </p>
                            </div>
                            <div className="flex items-center justify-end mt-0.5 space-x-2">
                              <div className="cursor-pointer">
                                {meeting.noticePdfUrl && (
                                  <a
                                    href={meeting.noticePdfUrl}
                                    className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Notice
                                  </a>
                                )}
                              </div>
                              {platform?.allowSupplemental &&
                                meeting.attachments?.length > 0 && (
                                  <Tippy
                                    content="Supplemental Materials"
                                    placement="top"
                                    animation="scale"
                                    delay={[500, 0]}
                                  >
                                    <button
                                      onClick={() =>
                                        setSupplemental({
                                          visible: true,
                                          meeting: meeting,
                                        })
                                      }
                                      className="cursor-pointer"
                                      aria-label="Supplemental Materials"
                                    >
                                      <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                    </button>
                                  </Tippy>
                                )}

                              {meeting.videoUrl && (
                                <>
                                  {meeting.pdfOnly ? (
                                    <a
                                      href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                    >
                                      Watch
                                    </a>
                                  ) : (
                                    <a
                                      href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                    >
                                      Watch
                                    </a>
                                  )}
                                </>
                              )}
                              {meeting.pdfOnly ? (
                                <Link
                                  href="/engage/[client]/agenda-pdf/[agenda]"
                                  as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                >
                                  <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                    Agenda
                                  </a>
                                </Link>
                              ) : (
                                <Link
                                  href="/engage/[client]/[agenda]"
                                  as={`/engage/${client}/${meeting.id}`}
                                >
                                  <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                    Agenda
                                  </a>
                                </Link>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      {(meeting.publishStatus === "canceled" ||
                        meeting.publishStatus === "adjourned") && (
                        <div>
                          <div
                            className={clsx(
                              uIdx % 2 !== 0
                                ? "bg-gray-50/50 border-gray-300"
                                : "bg-gray-100 border-gray-200",
                              "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200",
                            )}
                          >
                            <div className="col-span-2">
                              <p className="text-sm font-medium text-black">
                                {meeting.meetingType}
                              </p>
                              <p className="mt-1 flex items-center text-sm text-gray-900">
                                <span className="truncate">
                                  {meeting.room.roomName}
                                </span>
                              </p>
                              <p className="mt-1 flex items-center text-sm text-gray-900">
                                <span className="truncate">
                                  {meeting.scheduleDate}
                                </span>
                              </p>
                            </div>
                            <div className="flex items-center justify-end mt-0.5 space-x-2">
                              <div className="cursor-pointer">
                                {meeting.noticePdfUrl && (
                                  <a
                                    href={meeting.noticePdfUrl}
                                    className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Notice
                                  </a>
                                )}
                              </div>
                              {platform?.allowSupplemental &&
                                meeting.attachments?.length > 0 && (
                                  <Tippy
                                    content="Supplemental Materials"
                                    placement="top"
                                    animation="scale"
                                    delay={[500, 0]}
                                  >
                                    <button
                                      onClick={() =>
                                        setSupplemental({
                                          visible: true,
                                          meeting: meeting,
                                        })
                                      }
                                      className="cursor-pointer"
                                      aria-label="Supplemental Materials"
                                    >
                                      <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                    </button>
                                  </Tippy>
                                )}
                              {meeting.pdfOnly ? (
                                <Link
                                  href="/engage/[client]/agenda-pdf/[agenda]"
                                  as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                >
                                  <a className="text-red-600 hover:text-indigo-900 underline cursor-pointer">
                                    Meeting{" "}
                                    {meeting.publishStatus === "canceled"
                                      ? "Canceled"
                                      : "Adjourned"}
                                  </a>
                                </Link>
                              ) : (
                                <Link
                                  href="/engage/[client]/cancelled/[id]"
                                  as={`/engage/${client}/cancelled/${meeting.id}`}
                                >
                                  <a className="mt-1 text-sm text-red-600 hover:text-indigo-900 cursor-pointer select-none">
                                    Meeting{" "}
                                    {meeting.publishStatus === "canceled"
                                      ? "Canceled"
                                      : "Adjourned"}
                                  </a>
                                </Link>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
              })}
            </div>
          </div>
          {/* Mobile Data */}
          <div className="sm:hidden overflow-hidden sm:rounded-md mt-2">
            <div>
              {upcomingList?.map((meeting, mIdx) => {
                if (board) {
                  if (board === meeting.department._id) {
                    return (
                      <div key={`upcoming-mobile-${mIdx}`}>
                        {(meeting.publishStatus === "published" ||
                          meeting.publishStatus === "completed") && (
                          <>
                            <div className="">
                              <div className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                                <div>
                                  <p className="text-sm font-medium text-black">
                                    {meeting.meetingType}
                                  </p>
                                  <p className="mt-1 flex items-center text-sm text-gray-900">
                                    <span className="truncate">
                                      {meeting.scheduleDate}
                                    </span>
                                  </p>
                                </div>
                                <div className="flex items-center">
                                  {meeting.noticePdfUrl && (
                                    <div className="cursor-pointer">
                                      <a
                                        href={meeting.noticePdfUrl}
                                        className="flex py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        Notice
                                      </a>
                                    </div>
                                  )}
                                  {meeting.videoUrl && (
                                    <>
                                      {meeting.pdfOnly ? (
                                        <a
                                          href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-blue-700 hover:text-blue-800 underline cursor-pointer mr-2"
                                        >
                                          Watch
                                        </a>
                                      ) : (
                                        <a
                                          href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-blue-700 hover:text-blue-800 underline cursor-pointer mr-2"
                                        >
                                          Watch
                                        </a>
                                      )}
                                    </>
                                  )}
                                  {meeting.hasLiveFeed && (
                                    <>
                                      {meeting.pdfOnly ? (
                                        <a
                                          href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/live/${client}/${meeting.id}`}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline mr-2"
                                        >
                                          Watch
                                        </a>
                                      ) : (
                                        <a
                                          href={`${process.env.NEXT_PUBLIC_BASE_URL}/live-player/${client}/${meeting.id}`}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline mr-2"
                                        >
                                          Watch
                                        </a>
                                      )}
                                    </>
                                  )}
                                  {meeting.pdfOnly ? (
                                    <Link
                                      href="/engage/[client]/agenda-pdf/[agenda]"
                                      as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                    >
                                      <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                        Agenda
                                      </a>
                                    </Link>
                                  ) : (
                                    <Link
                                      href="/engage/[client]/[agenda]"
                                      as={`/engage/${client}/${meeting.id}`}
                                    >
                                      <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                        Agenda
                                      </a>
                                    </Link>
                                  )}
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                        {meeting.publishStatus === "minutes" && (
                          <div>
                            <div className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                              <div>
                                <p className="text-sm font-medium text-black">
                                  {meeting.meetingType}
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.scheduleDate}
                                  </span>
                                </p>
                              </div>
                              <div className="flex items-center">
                                {meeting.noticePdfUrl && (
                                  <div className="cursor-pointer">
                                    <a
                                      href={meeting.noticePdfUrl}
                                      className="flex py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Notice
                                    </a>
                                  </div>
                                )}
                                {meeting.pdfOnly ? (
                                  <Link
                                    href="/engage/[client]/agenda-pdf/[agenda]"
                                    as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                      Agenda
                                    </a>
                                  </Link>
                                ) : (
                                  <Link
                                    href="/engage/[client]/[agenda]"
                                    as={`/engage/${client}/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                      Agenda
                                    </a>
                                  </Link>
                                )}
                                {meeting.minutesPdfOnly ? (
                                  <Link
                                    href="/engage/[client]/minutes-pdf/[agenda]"
                                    as={`/engage/${client}/minutes-pdf/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                      Minutes
                                    </a>
                                  </Link>
                                ) : (
                                  <Link
                                    href="/engage/[client]/minutes/[minutes]"
                                    as={`/engage/${client}/minutes/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                      Minutes
                                    </a>
                                  </Link>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        {meeting.publishStatus === "manualminutes" && (
                          <div>
                            <div className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                              <div>
                                <p className="text-sm font-medium text-black">
                                  {meeting.meetingType}
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.scheduleDate}
                                  </span>
                                </p>
                              </div>
                              <div className="flex items-center">
                                {meeting.noticePdfUrl && (
                                  <div className="cursor-pointer">
                                    <a
                                      href={meeting.noticePdfUrl}
                                      className="flex py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Notice
                                    </a>
                                  </div>
                                )}
                                {meeting.pdfOnly ? (
                                  <Link
                                    href="/engage/[client]/agenda-pdf/[agenda]"
                                    as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                      Agenda
                                    </a>
                                  </Link>
                                ) : (
                                  <Link
                                    href="/engage/[client]/[agenda]"
                                    as={`/engage/${client}/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                      Agenda
                                    </a>
                                  </Link>
                                )}
                                {meeting.minutesPdfOnly ? (
                                  <Link
                                    href="/engage/[client]/minutes-pdf/[agenda]"
                                    as={`/engage/${client}/minutes-pdf/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                      Minutes
                                    </a>
                                  </Link>
                                ) : (
                                  <Link
                                    href="/engage/[client]/minutes/[minutes]"
                                    as={`/engage/${client}/minutes/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                      Minutes
                                    </a>
                                  </Link>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        {(meeting.publishStatus === "draft" ||
                          meeting.publishStatus === "draftreview") &&
                          client !== "broomfield" && (
                            <div className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                              <div>
                                <p className="text-sm font-medium text-black">
                                  {meeting.meetingType}
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.scheduleDate}
                                  </span>
                                </p>
                              </div>
                              <div className="flex items-center space-x-2">
                                {meeting.noticePdfUrl && (
                                  <div className="cursor-pointer">
                                    <a
                                      href={meeting.noticePdfUrl}
                                      className="flex py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Notice
                                    </a>
                                  </div>
                                )}
                                <p className="mt-1 text-sm text-black">
                                  Agenda Pending
                                </p>
                              </div>
                            </div>
                          )}
                        {meeting.publishStatus === "draftreview" &&
                          client === "broomfield" && (
                            <>
                              <div className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                                <div>
                                  <p className="text-sm font-medium text-black">
                                    {meeting.meetingType}
                                  </p>
                                  <p className="mt-1 flex items-center text-sm text-gray-900">
                                    <span className="truncate">
                                      {meeting.scheduleDate}
                                    </span>
                                  </p>
                                </div>
                                <div className="flex items-center">
                                  {meeting.noticePdfUrl && (
                                    <div className="cursor-pointer">
                                      <a
                                        href={meeting.noticePdfUrl}
                                        className="flex py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        Notice
                                      </a>
                                    </div>
                                  )}
                                  <Link
                                    href="/engage/[id]/[agenda]"
                                    as={`/engage/${client}/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                      Draft Available
                                    </a>
                                  </Link>
                                </div>
                              </div>
                            </>
                          )}
                        {meeting.publishStatus === "review" && (
                          <>
                            <div className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                              <div>
                                <p className="text-sm font-medium text-black">
                                  {meeting.meetingType}
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.scheduleDate}
                                  </span>
                                </p>
                              </div>
                              <div className="flex items-center">
                                {meeting.noticePdfUrl && (
                                  <div className="cursor-pointer">
                                    <a
                                      href={meeting.noticePdfUrl}
                                      className="flex py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      Notice
                                    </a>
                                  </div>
                                )}
                                {meeting.pdfOnly ? (
                                  <Link
                                    href="/engage/[client]/agenda-pdf/[agenda]"
                                    as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                      Agenda
                                    </a>
                                  </Link>
                                ) : (
                                  <Link
                                    href="/engage/[client]/[agenda]"
                                    as={`/engage/${client}/${meeting.id}`}
                                  >
                                    <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                      Agenda
                                    </a>
                                  </Link>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                        {(meeting.publishStatus === "canceled" ||
                          meeting.publishStatus === "adjourned") && (
                          <div className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                            <div>
                              <p className="text-sm font-medium text-black">
                                {meeting.meetingType}
                              </p>
                              <p className="mt-1 flex items-center text-sm text-gray-900">
                                <span className="truncate">
                                  {meeting.scheduleDate}
                                </span>
                              </p>
                            </div>
                            <div className="flex items-center">
                              {meeting.noticePdfUrl && (
                                <div className="cursor-pointer">
                                  <a
                                    href={meeting.noticePdfUrl}
                                    className="flex py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Notice
                                  </a>
                                </div>
                              )}
                              {meeting.pdfOnly ? (
                                <Link
                                  href="/engage/[client]/agenda-pdf/[agenda]"
                                  as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                >
                                  <a className="text-red-600 hover:text-indigo-900 underline cursor-pointer">
                                    Meeting{" "}
                                    {meeting.publishStatus === "canceled"
                                      ? "Canceled"
                                      : "Adjourned"}
                                  </a>
                                </Link>
                              ) : (
                                <Link
                                  href="/engage/[client]/cancelled/[id]"
                                  as={`/engage/${client}/cancelled/${meeting.id}`}
                                >
                                  <a className="mt-1 text-sm text-red-600">
                                    Meeting{" "}
                                    {meeting.publishStatus === "canceled"
                                      ? "Canceled"
                                      : "Adjourned"}
                                  </a>
                                </Link>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  } else {
                    return null;
                  }
                } else {
                  return (
                    <div key={`upcoming-mobile-${mIdx}`}>
                      {(meeting.publishStatus === "published" ||
                        meeting.publishStatus === "completed") && (
                        <>
                          {meeting.hasLiveFeed ? (
                            <>
                              {meeting.pdfOnly ? (
                                <a
                                  href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/live/${client}/${meeting.id}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]"
                                >
                                  <div>
                                    <p className="text-sm font-medium text-black">
                                      {meeting.meetingType}
                                    </p>
                                    <p className="mt-1 flex items-center text-sm text-gray-900">
                                      <span className="truncate">
                                        {meeting.scheduleDate}
                                      </span>
                                    </p>
                                  </div>
                                  <p className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                    Watch
                                  </p>
                                </a>
                              ) : (
                                <a
                                  href={`${process.env.NEXT_PUBLIC_BASE_URL}/live-player/${client}/${meeting.id}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]"
                                >
                                  <div>
                                    <p className="text-sm font-medium text-black">
                                      {meeting.meetingType}
                                    </p>
                                    <p className="mt-1 flex items-center text-sm text-gray-900">
                                      <span className="truncate">
                                        {meeting.scheduleDate}
                                      </span>
                                    </p>
                                  </div>
                                  <p className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                    Watch
                                  </p>
                                </a>
                              )}
                            </>
                          ) : (
                            <>
                              {meeting.pdfOnly ? (
                                <Link
                                  href="/engage/[client]/agenda-pdf/[agenda]"
                                  as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                >
                                  <a className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                                    <div>
                                      <p className="text-sm font-medium text-black">
                                        {meeting.meetingType}
                                      </p>
                                      <p className="mt-1 flex items-center text-sm text-gray-900">
                                        <span className="truncate">
                                          {meeting.scheduleDate}
                                        </span>
                                      </p>
                                    </div>
                                    <p className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                      Agenda
                                    </p>
                                  </a>
                                </Link>
                              ) : (
                                <Link
                                  href="/engage/[client]/[agenda]"
                                  as={`/engage/${client}/${meeting.id}`}
                                >
                                  <a className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                                    <div>
                                      <p className="text-sm font-medium text-black">
                                        {meeting.meetingType}
                                      </p>
                                      <p className="mt-1 flex items-center text-sm text-gray-900">
                                        <span className="truncate">
                                          {meeting.scheduleDate}
                                        </span>
                                      </p>
                                    </div>
                                    <p className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                      Agenda
                                    </p>
                                  </a>
                                </Link>
                              )}
                            </>
                          )}
                        </>
                      )}
                      {meeting.publishStatus === "minutes" && (
                        <>
                          {meeting.minutesPdfOnly ? (
                            <Link
                              href="/engage/[client]/minutes-pdf/[agenda]"
                              as={`/engage/${client}/minutes-pdf/${meeting.id}`}
                            >
                              <a className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                                <div>
                                  <p className="text-sm font-medium text-black">
                                    {meeting.meetingType}
                                  </p>
                                  <p className="mt-1 flex items-center text-sm text-gray-900">
                                    <span className="truncate">
                                      {meeting.scheduleDate}
                                    </span>
                                  </p>
                                </div>
                                <p className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                  Minutes
                                </p>
                              </a>
                            </Link>
                          ) : (
                            <Link
                              href="/engage/[client]/minutes/[minutes]"
                              as={`/engage/${client}/minutes/${meeting.id}`}
                            >
                              <a className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                                <div>
                                  <p className="text-sm font-medium text-black">
                                    {meeting.meetingType}
                                  </p>
                                  <p className="mt-1 flex items-center text-sm text-gray-900">
                                    <span className="truncate">
                                      {meeting.scheduleDate}
                                    </span>
                                  </p>
                                </div>
                                <p className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                  Minutes
                                </p>
                              </a>
                            </Link>
                          )}
                        </>
                      )}
                      {meeting.publishStatus === "manualminutes" && (
                        <Link
                          href="/engage/[client]/[agenda]"
                          as={`/engage/${client}/${meeting.id}`}
                        >
                          <a className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                            <div>
                              <p className="text-sm font-medium text-black">
                                {meeting.meetingType}
                              </p>
                              <p className="mt-1 flex items-center text-sm text-gray-900">
                                <span className="truncate">
                                  {meeting.scheduleDate}
                                </span>
                              </p>
                            </div>
                            <p className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                              Minutes
                            </p>
                          </a>
                        </Link>
                      )}
                      {(meeting.publishStatus === "draft" ||
                        meeting.publishStatus === "draftreview") &&
                        client !== "broomfield" && (
                          <div className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                            <div>
                              <p className="text-sm font-medium text-black">
                                {meeting.meetingType}
                              </p>
                              <p className="mt-1 flex items-center text-sm text-gray-900">
                                <span className="truncate">
                                  {meeting.scheduleDate}
                                </span>
                              </p>
                            </div>
                            {meeting.noticePdfUrl && (
                              <div className="cursor-pointer">
                                <a
                                  href={meeting.noticePdfUrl}
                                  className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Notice
                                </a>
                              </div>
                            )}
                            <p className="mt-1 text-sm text-black">
                              Agenda Pending
                            </p>
                          </div>
                        )}
                      {meeting.publishStatus === "draftreview" &&
                        client === "broomfield" && (
                          <Link
                            href="/engage/[id]/[agenda]"
                            as={`/engage/${client}/${meeting.id}`}
                          >
                            <a className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                              <div>
                                <p className="text-sm font-medium text-black">
                                  {meeting.meetingType}
                                </p>
                                <p className="mt-1 flex items-center text-sm text-gray-900">
                                  <span className="truncate">
                                    {meeting.scheduleDate}
                                  </span>
                                </p>
                              </div>
                              <p className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline">
                                Draft Available
                              </p>
                            </a>
                          </Link>
                        )}
                      {meeting.publishStatus === "review" && (
                        <>
                          {meeting.pdfOnly ? (
                            <Link
                              href="/engage/[client]/agenda-pdf/[agenda]"
                              as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                            >
                              <a className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                                <div>
                                  <p className="text-sm font-medium text-black">
                                    {meeting.meetingType}
                                  </p>
                                  <p className="mt-1 flex items-center text-sm text-gray-900">
                                    <span className="truncate">
                                      {meeting.scheduleDate}
                                    </span>
                                  </p>
                                </div>

                                <p className="mt-1 text-sm text-black">
                                  Agenda
                                </p>
                              </a>
                            </Link>
                          ) : (
                            <Link
                              href="/engage/[client]/[agenda]"
                              as={`/engage/${client}/${meeting.id}`}
                            >
                              <a className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                                <div>
                                  <p className="text-sm font-medium text-black">
                                    {meeting.meetingType}
                                  </p>
                                  <p className="mt-1 flex items-center text-sm text-gray-900">
                                    <span className="truncate">
                                      {meeting.scheduleDate}
                                    </span>
                                  </p>
                                </div>

                                <p className="mt-1 text-sm text-black">
                                  Agenda
                                </p>
                              </a>
                            </Link>
                          )}
                        </>
                      )}
                      {(meeting.publishStatus === "canceled" ||
                        meeting.publishStatus === "adjourned") && (
                        <>
                          {meeting.pdfOnly ? (
                            <Link
                              href="/engage/[client]/agenda-pdf/[agenda]"
                              as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                            >
                              <a className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                                <div>
                                  <p className="text-sm font-medium text-black">
                                    {meeting.meetingType}
                                  </p>
                                  <p className="mt-1 flex items-center text-sm text-gray-900">
                                    <span className="truncate">
                                      {meeting.scheduleDate}
                                    </span>
                                  </p>
                                </div>

                                <p className="mt-1 text-sm text-red-600">
                                  Meeting{" "}
                                  {meeting.publishStatus === "canceled"
                                    ? "Canceled"
                                    : "Adjourned"}
                                </p>
                              </a>
                            </Link>
                          ) : (
                            <Link
                              href="/engage/[client]/cancelled/[id]"
                              as={`/engage/${client}/cancelled/${meeting.id}`}
                            >
                              <a className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                                <div>
                                  <p className="text-sm font-medium text-black">
                                    {meeting.meetingType}
                                  </p>
                                  <p className="mt-1 flex items-center text-sm text-gray-900">
                                    <span className="truncate">
                                      {meeting.scheduleDate}
                                    </span>
                                  </p>
                                </div>

                                <p className="mt-1 text-sm text-red-600">
                                  Meeting{" "}
                                  {meeting.publishStatus === "canceled"
                                    ? "Canceled"
                                    : "Adjourned"}
                                </p>
                              </a>
                            </Link>
                          )}
                        </>
                      )}
                    </div>
                  );
                }
              })}
            </div>
          </div>
        </>
      )}

      {/* All Meetings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <p className="md:col-span-2 font-bold">All Meetings:</p>
        <div className="flex flex-col">
          <p className="italic text-gray-700">Meeting List:</p>
          {selected && (
            <Listbox
              value={selected}
              onChange={(e) => handleChange("department", e)}
            >
              {({ open }) => (
                <>
                  <div className="relative mt-1">
                    <Listbox.Button className="relative w-full py-2 lg:py-4 pl-3 pr-10 text-left bg-white border border-gray-300 rounded-lg shadow-md cursor-default focus:outline-none sm:text-sm">
                      <span className="block truncate">{selected.name}</span>
                      <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <SelectorIcon
                          className="w-5 h-5 text-black"
                          aria-hidden="true"
                        />
                      </span>
                    </Listbox.Button>
                    <Transition
                      show={open}
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options
                        static
                        className="absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                      >
                        <Listbox.Option
                          className={({ active }) =>
                            `${
                              active
                                ? "text-blue-900 bg-blue-100"
                                : "text-gray-900"
                            }
                            cursor-default select-none relative py-2 pl-10 pr-4`
                          }
                          value={{ name: "All", id: "all" }}
                        >
                          {({ selected, active }) => (
                            <>
                              <span
                                className={`${
                                  selected ? "font-medium" : "font-normal"
                                } block truncate`}
                              >
                                All
                              </span>
                              {selected ? (
                                <span
                                  className={`${
                                    active ? "text-blue-700" : "text-blue-700"
                                  }
                                  absolute inset-y-0 left-0 flex items-center pl-3`}
                                >
                                  <CheckIcon
                                    className="w-5 h-5"
                                    aria-hidden="true"
                                  />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                        {departments?.map((department, personIdx) => (
                          <Listbox.Option
                            key={personIdx}
                            className={({ active }) =>
                              `${
                                active
                                  ? "text-blue-900 bg-blue-100"
                                  : "text-gray-900"
                              }
                            cursor-default select-none relative py-2 pl-10 pr-4`
                            }
                            value={department}
                          >
                            {({ selected, active }) => (
                              <>
                                <span
                                  className={`${
                                    selected ? "font-medium" : "font-normal"
                                  } block truncate`}
                                >
                                  {department?.name}
                                </span>
                                {selected ? (
                                  <span
                                    className={`${
                                      active ? "text-blue-700" : "text-blue-700"
                                    }
                                  absolute inset-y-0 left-0 flex items-center pl-3`}
                                  >
                                    <CheckIcon
                                      className="w-5 h-5"
                                      aria-hidden="true"
                                    />
                                  </span>
                                ) : null}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </>
              )}
            </Listbox>
          )}
        </div>
        <div className="flex flex-col">
          <p className="italic text-gray-700">Meeting Date:</p>
          <Listbox
            value={selectedDate}
            onChange={(e) => handleChange("date", e)}
          >
            {({ open }) => (
              <>
                <div className="relative mt-1">
                  <Listbox.Button className="relative w-full py-2 lg:py-4 pl-3 pr-10 text-left bg-white border border-gray-300 rounded-lg shadow-md cursor-default focus:outline-none sm:text-sm">
                    <span className="block truncate">{selectedDate.name}</span>
                    <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <SelectorIcon
                        className="w-5 h-5 text-black"
                        aria-hidden="true"
                      />
                    </span>
                  </Listbox.Button>
                  <Transition
                    show={open}
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options
                      static
                      className="absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                    >
                      {dates?.map((person, personIdx) => (
                        <Listbox.Option
                          key={personIdx}
                          className={({ active }) =>
                            `${
                              active
                                ? "text-blue-900 bg-blue-100"
                                : "text-gray-900"
                            }
                            cursor-default select-none relative py-2 pl-10 pr-4`
                          }
                          value={person}
                        >
                          {({ selected, active }) => (
                            <>
                              <span
                                className={`${
                                  selected ? "font-medium" : "font-normal"
                                } block truncate`}
                              >
                                {person.name}
                              </span>
                              {selected ? (
                                <span
                                  className={`${
                                    active ? "text-blue-700" : "text-blue-700"
                                  }
                                  absolute inset-y-0 left-0 flex items-center pl-3`}
                                >
                                  <CheckIcon
                                    className="w-5 h-5"
                                    aria-hidden="true"
                                  />
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </>
            )}
          </Listbox>
        </div>
      </div>

      {/* Data Table */}
      <div className="hidden xl:flex xl:flex-col mt-8">
        <div className="-my-2 overflow-x-auto sm:-mx-6 2xl:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full px-2 2xl:px-8">
            <div className="shadow overflow-hidden border border-gray-400 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Meeting Type
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Date / Time
                    </th>

                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Details</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMeetings?.length === 0 && fetching && (
                    <>
                      <tr className="bg-white">
                        <motion.td
                          initial={{ opacity: 0.5 }}
                          animate={{ opacity: 1 }}
                          transition={{
                            repeat: Infinity,
                            repeatType: "mirror",
                            duration: 0.8, // Adjust the animation duration
                            ease: "linear",
                          }}
                          className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 blur-sm"
                        >
                          placeholder
                        </motion.td>
                        <motion.td
                          initial={{ opacity: 0.5 }}
                          animate={{ opacity: 1 }}
                          transition={{
                            repeat: Infinity,
                            repeatType: "mirror",
                            duration: 0.8, // Adjust the animation duration
                            ease: "linear",
                          }}
                          className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 blur-sm"
                        >
                          placeholder
                        </motion.td>

                        <motion.td
                          initial={{ opacity: 0.5 }}
                          animate={{ opacity: 1 }}
                          transition={{
                            repeat: Infinity,
                            repeatType: "mirror",
                            duration: 0.8, // Adjust the animation duration
                            ease: "linear",
                          }}
                          className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"
                        >
                          <div className="text-black select-none blur-sm">
                            Agenda Pending
                          </div>
                        </motion.td>
                      </tr>
                      <tr className="bg-white">
                        <motion.td
                          initial={{ opacity: 0.5 }}
                          animate={{ opacity: 1 }}
                          transition={{
                            repeat: Infinity,
                            repeatType: "mirror",
                            duration: 0.8, // Adjust the animation duration
                            ease: "linear",
                          }}
                          className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 blur-sm"
                        >
                          placeholder
                        </motion.td>
                        <motion.td
                          initial={{ opacity: 0.5 }}
                          animate={{ opacity: 1 }}
                          transition={{
                            repeat: Infinity,
                            repeatType: "mirror",
                            duration: 0.8, // Adjust the animation duration
                            ease: "linear",
                          }}
                          className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 blur-sm"
                        >
                          placeholder
                        </motion.td>

                        <motion.td
                          initial={{ opacity: 0.5 }}
                          animate={{ opacity: 1 }}
                          transition={{
                            repeat: Infinity,
                            repeatType: "mirror",
                            duration: 0.8, // Adjust the animation duration
                            ease: "linear",
                          }}
                          className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"
                        >
                          <div className="text-black select-none blur-sm">
                            Agenda Pending
                          </div>
                        </motion.td>
                      </tr>
                    </>
                  )}
                  {!fetching && (
                    <tr className="bg-gray-50">
                      <td className="pl-2 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center">
                          <MdFilterList className="h-5 w-5 text-gray-500" />
                          <input
                            type="search"
                            value={filterText?.board}
                            onChange={(e) =>
                              applyFilter({
                                type: "FILTER_BOARD",
                                payload: e.target.value,
                              })
                            }
                            className="w-full bg-transparent text-black text-sm placeholder:text-zinc-400 border border-b-blue-600 border-t-transparent border-r-transparent border-l-transparent focus:outline-none active:outline-none"
                            placeholder="Filter by Type"
                          />
                        </div>
                      </td>
                      <td className="pl-2 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center">
                          <MdFilterList className="h-5 w-5 text-gray-500" />
                          <input
                            type="search"
                            value={filterText?.date}
                            onChange={(e) =>
                              applyFilter({
                                type: "FILTER_DATE",
                                payload: e.target.value,
                              })
                            }
                            className="w-full bg-transparent text-black text-sm placeholder:text-zinc-400 border border-b-blue-600 border-t-transparent border-r-transparent border-l-transparent focus:outline-none active:outline-none"
                            placeholder="Filter by Date"
                          />
                        </div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"></td>
                    </tr>
                  )}
                  {filteredMeetings?.map((meeting, personIdx) => (
                    <tr
                      key={meeting.id}
                      className={
                        personIdx % 2 === 0 ? "bg-white" : "bg-gray-100"
                      }
                    >
                      <td className="px-2 2xl:px-6 py-4 text-sm font-medium text-gray-900">
                        <div>{meeting.meetingType}</div>
                        <div>{meeting.room.roomName}</div>
                      </td>
                      <td className="px-2 2xl:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {meeting.scheduleDate}
                      </td>
                      <td className="px-2 2xl:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {(meeting.publishStatus === "published" ||
                          meeting.publishStatus === "completed") && (
                          <div className="inline-flex space-x-2 xl:space-x-6">
                            <div className="cursor-pointer">
                              {meeting.noticePdfUrl && (
                                <a
                                  href={meeting.noticePdfUrl}
                                  className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Notice
                                </a>
                              )}
                            </div>

                            {platform?.allowSupplemental &&
                              meeting.attachments?.length > 0 && (
                                <Tippy
                                  content="Supplemental Materials"
                                  placement="top"
                                  animation="scale"
                                  delay={[500, 0]}
                                >
                                  <button
                                    onClick={() =>
                                      setSupplemental({
                                        visible: true,
                                        meeting: meeting,
                                      })
                                    }
                                    className="cursor-pointer"
                                    aria-label="Supplemental Materials"
                                  >
                                    <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                  </button>
                                </Tippy>
                              )}
                            {meeting.videoUrl && (
                              <>
                                {meeting.pdfOnly ? (
                                  <a
                                    href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                  >
                                    Watch
                                  </a>
                                ) : (
                                  <a
                                    href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                  >
                                    Watch
                                  </a>
                                )}
                              </>
                            )}
                            {meeting.hasLiveFeed && (
                              <>
                                {meeting.pdfOnly ? (
                                  <a
                                    href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/live/${client}/${meeting.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                  >
                                    Watch
                                  </a>
                                ) : (
                                  <a
                                    href={`${process.env.NEXT_PUBLIC_BASE_URL}/live-player/${client}/${meeting.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                  >
                                    Watch
                                  </a>
                                )}
                              </>
                            )}
                            {meeting.pdfOnly ? (
                              <Link
                                href="/engage/[client]/agenda-pdf/[agenda]"
                                as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                              >
                                <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                  Agenda
                                </a>
                              </Link>
                            ) : (
                              <Link
                                href="/engage/[client]/[agenda]"
                                as={`/engage/${client}/${meeting.id}`}
                              >
                                <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                  Agenda
                                </a>
                              </Link>
                            )}
                          </div>
                        )}
                        {meeting.publishStatus === "draftreview" &&
                          client === "broomfield" && (
                            <div className="flex items-center space-x-2 xl:space-x-6 justify-between">
                              <div className="cursor-pointer">
                                {meeting.noticePdfUrl && (
                                  <a
                                    href={meeting.noticePdfUrl}
                                    className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Notice
                                  </a>
                                )}
                              </div>
                              {meeting.pdfOnly ? (
                                <Link
                                  href="/engage/[client]/agenda-pdf/[agenda]"
                                  as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                                >
                                  <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                    Draft Available
                                  </a>
                                </Link>
                              ) : (
                                <Link
                                  href="/engage/[client]/[agenda]"
                                  as={`/engage/${client}/${meeting.id}`}
                                >
                                  <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                    Draft Available
                                  </a>
                                </Link>
                              )}
                            </div>
                          )}
                        {meeting.publishStatus === "minutes" && (
                          <div className="inline-flex space-x-2 xl:space-x-6">
                            <div className="cursor-pointer">
                              {meeting.noticePdfUrl && (
                                <a
                                  href={meeting.noticePdfUrl}
                                  className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Notice
                                </a>
                              )}
                            </div>

                            {platform?.allowSupplemental &&
                              meeting.attachments?.length > 0 && (
                                <Tippy
                                  content="Supplemental Materials"
                                  placement="top"
                                  animation="scale"
                                  delay={[500, 0]}
                                >
                                  <button
                                    onClick={() =>
                                      setSupplemental({
                                        visible: true,
                                        meeting: meeting,
                                      })
                                    }
                                    className="cursor-pointer"
                                    aria-label="Supplemental Materials"
                                  >
                                    <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                  </button>
                                </Tippy>
                              )}
                            {meeting.videoUrl && (
                              <>
                                {meeting.pdfOnly ? (
                                  <a
                                    href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                  >
                                    Watch
                                  </a>
                                ) : (
                                  <a
                                    href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                  >
                                    Watch
                                  </a>
                                )}
                              </>
                            )}
                            {meeting.pdfOnly ? (
                              <Link
                                href="/engage/[client]/agenda-pdf/[agenda]"
                                as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                              >
                                <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                  Agenda
                                </a>
                              </Link>
                            ) : (
                              <Link
                                href="/engage/[client]/[agenda]"
                                as={`/engage/${client}/${meeting.id}`}
                              >
                                <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                  Agenda
                                </a>
                              </Link>
                            )}
                            {meeting.minutesPdfOnly ? (
                              <Link
                                href="/engage/[client]/minutes-pdf/[agenda]"
                                as={`/engage/${client}/minutes-pdf/${meeting.id}`}
                              >
                                <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                  Minutes
                                </a>
                              </Link>
                            ) : (
                              <Link
                                href="/engage/[client]/minutes/[minutes]"
                                as={`/engage/${client}/minutes/${meeting.id}`}
                              >
                                <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                  Minutes
                                </a>
                              </Link>
                            )}
                          </div>
                        )}
                        {meeting.publishStatus === "manualminutes" && (
                          <div className="inline-flex space-x-2 xl:space-x-6">
                            <div className="cursor-pointer">
                              {meeting.noticePdfUrl && (
                                <a
                                  href={meeting.noticePdfUrl}
                                  className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Notice
                                </a>
                              )}
                            </div>

                            {platform?.allowSupplemental &&
                              meeting.attachments?.length > 0 && (
                                <Tippy
                                  content="Supplemental Materials"
                                  placement="top"
                                  animation="scale"
                                  delay={[500, 0]}
                                >
                                  <button
                                    onClick={() =>
                                      setSupplemental({
                                        visible: true,
                                        meeting: meeting,
                                      })
                                    }
                                    className="cursor-pointer"
                                    aria-label="Supplemental Materials"
                                  >
                                    <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                  </button>
                                </Tippy>
                              )}
                            {meeting.videoUrl && (
                              <>
                                {meeting.pdfOnly ? (
                                  <a
                                    href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                  >
                                    Watch
                                  </a>
                                ) : (
                                  <a
                                    href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                  >
                                    Watch
                                  </a>
                                )}
                              </>
                            )}
                            {meeting.pdfOnly ? (
                              <Link
                                href="/engage/[client]/agenda-pdf/[agenda]"
                                as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                              >
                                <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                  Agenda
                                </a>
                              </Link>
                            ) : (
                              <Link
                                href="/engage/[client]/[agenda]"
                                as={`/engage/${client}/${meeting.id}`}
                              >
                                <a>
                                  <div className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                    Agenda
                                  </div>
                                </a>
                              </Link>
                            )}
                            {meeting.minutesPdfOnly ? (
                              <Link
                                href="/engage/[client]/minutes-pdf/[agenda]"
                                as={`/engage/${client}/minutes-pdf/${meeting.id}`}
                              >
                                <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                  Minutes
                                </a>
                              </Link>
                            ) : (
                              <Link
                                href="/engage/[client]/minutes/[minutes]"
                                as={`/engage/${client}/minutes/${meeting.id}`}
                              >
                                <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                  Minutes
                                </a>
                              </Link>
                            )}
                          </div>
                        )}
                        {meeting.publishStatus === "review" && (
                          <div className="inline-flex space-x-2 xl:space-x-6">
                            <div className="cursor-pointer">
                              {meeting.noticePdfUrl && (
                                <a
                                  href={meeting.noticePdfUrl}
                                  className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Notice
                                </a>
                              )}
                            </div>

                            {platform?.allowSupplemental &&
                              meeting.attachments?.length > 0 && (
                                <Tippy
                                  content="Supplemental Materials"
                                  placement="top"
                                  animation="scale"
                                  delay={[500, 0]}
                                >
                                  <button
                                    onClick={() =>
                                      setSupplemental({
                                        visible: true,
                                        meeting: meeting,
                                      })
                                    }
                                    className="cursor-pointer"
                                    aria-label="Supplemental Materials"
                                  >
                                    <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                  </button>
                                </Tippy>
                              )}
                            {meeting.videoUrl && (
                              <>
                                {meeting.pdfOnly ? (
                                  <a
                                    href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                  >
                                    Watch
                                  </a>
                                ) : (
                                  <a
                                    href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-blue-700 hover:text-blue-800 underline cursor-pointer"
                                  >
                                    Watch
                                  </a>
                                )}
                              </>
                            )}
                            {meeting.pdfOnly ? (
                              <Link
                                href="/engage/[client]/agenda-pdf/[agenda]"
                                as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                              >
                                <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                  Agenda
                                </a>
                              </Link>
                            ) : (
                              <Link
                                href="/engage/[client]/[agenda]"
                                as={`/engage/${client}/${meeting.id}`}
                              >
                                <a className="text-blue-700 hover:text-blue-800 underline cursor-pointer">
                                  Agenda
                                </a>
                              </Link>
                            )}
                          </div>
                        )}
                        {(meeting.publishStatus === "draft" ||
                          meeting.publishStatus === "draftreview") &&
                          client !== "broomfield" && (
                            <div className="flex items-center justify-between space-x-2">
                              <div className="cursor-pointer">
                                {meeting.noticePdfUrl && (
                                  <a
                                    href={meeting.noticePdfUrl}
                                    className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Notice
                                  </a>
                                )}
                              </div>

                              <div className="text-black select-none">
                                Agenda Pending
                              </div>
                            </div>
                          )}
                        {(meeting.publishStatus === "canceled" ||
                          meeting.publishStatus === "adjourned") && (
                          <div className="inline-flex space-x-2 xl:space-x-6">
                            <div className="cursor-pointer">
                              {meeting.noticePdfUrl && (
                                <a
                                  href={meeting.noticePdfUrl}
                                  className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Notice
                                </a>
                              )}
                            </div>
                            {platform?.allowSupplemental &&
                              meeting.attachments?.length > 0 && (
                                <Tippy
                                  content="Supplemental Materials"
                                  placement="top"
                                  animation="scale"
                                  delay={[500, 0]}
                                >
                                  <button
                                    onClick={() =>
                                      setSupplemental({
                                        visible: true,
                                        meeting: meeting,
                                      })
                                    }
                                    className="cursor-pointer"
                                    aria-label="Supplemental Materials"
                                  >
                                    <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                                  </button>
                                </Tippy>
                              )}
                            {meeting.pdfOnly ? (
                              <Link
                                href="/engage/[client]/agenda-pdf/[agenda]"
                                as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                              >
                                <a className="text-red-600 hover:text-indigo-900 cursor-pointer select-none">
                                  Meeting{" "}
                                  {meeting.publishStatus === "canceled"
                                    ? "Canceled"
                                    : "Adjourned"}
                                </a>
                              </Link>
                            ) : (
                              <Link
                                href="/engage/[client]/cancelled/[id]"
                                as={`/engage/${client}/cancelled/${meeting.id}`}
                              >
                                <a className="text-red-600 hover:text-indigo-900 cursor-pointer select-none">
                                  Meeting{" "}
                                  {meeting.publishStatus === "canceled"
                                    ? "Canceled"
                                    : "Adjourned"}
                                </a>
                              </Link>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {/* Embed Data */}
      <div className="hidden sm:block xl:hidden overflow-hidden mt-8">
        <div>
          {filteredMeetings?.length === 0 && fetching && (
            <>
              <motion.div
                initial={{ opacity: 0.5 }}
                animate={{ opacity: 1 }}
                transition={{
                  repeat: Infinity,
                  repeatType: "mirror",
                  duration: 0.8, // Adjust the animation duration
                  ease: "linear",
                }}
                className="grid grid-cols-3 px-4 py-4 bg-gray-100 mb-6 border border-gray-200 rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200"
              >
                <div className="col-span-2">
                  <p className="text-sm font-medium text-black truncate blur-sm">
                    placeholder
                  </p>
                  <p className="mt-1 flex items-center text-sm text-gray-900 blur-sm">
                    placeholder
                  </p>
                </div>
                <p className="mt-1 text-sm text-gray-900 text-right blur-sm">
                  Agenda Pending
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0.5 }}
                animate={{ opacity: 1 }}
                transition={{
                  repeat: Infinity,
                  repeatType: "mirror",
                  duration: 0.8, // Adjust the animation duration
                  ease: "linear",
                }}
                className="grid grid-cols-3 px-4 py-4 bg-gray-100 mb-6 border border-gray-200 rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200"
              >
                <div className="col-span-2">
                  <p className="text-sm font-medium text-black truncate blur-sm">
                    placeholder
                  </p>
                  <p className="mt-1 flex items-center text-sm text-gray-900 blur-sm">
                    placeholder
                  </p>
                </div>
                <p className="mt-1 text-sm text-gray-900 text-right blur-sm">
                  Agenda Pending
                </p>
              </motion.div>
            </>
          )}
          {!fetching && (
            <div className="flex space-x-2 w-full mb-4">
              <div className="pl-2 whitespace-nowrap text-sm font-medium text-gray-900 w-1/2">
                <div className="flex items-center">
                  <MdFilterList className="h-5 w-5 text-gray-500" />
                  <input
                    type="search"
                    value={filterText?.board}
                    onChange={(e) =>
                      applyFilter({
                        type: "FILTER_BOARD",
                        payload: e.target.value,
                      })
                    }
                    className="w-full bg-transparent text-black text-sm placeholder:text-zinc-400 border border-b-blue-600 border-t-transparent border-r-transparent border-l-transparent focus:outline-none active:outline-none"
                    placeholder="Filter by Type"
                  />
                </div>
              </div>
              <div className="pl-2 whitespace-nowrap text-sm font-medium text-gray-900 w-1/2">
                <div className="flex items-center">
                  <MdFilterList className="h-5 w-5 text-gray-500" />
                  <input
                    type="search"
                    value={filterText?.date}
                    onChange={(e) =>
                      applyFilter({
                        type: "FILTER_DATE",
                        payload: e.target.value,
                      })
                    }
                    className="w-full bg-transparent text-black text-sm placeholder:text-zinc-400 border border-b-blue-600 border-t-transparent border-r-transparent border-l-transparent focus:outline-none active:outline-none"
                    placeholder="Filter by Date"
                  />
                </div>
              </div>
            </div>
          )}
          {filteredMeetings?.map((meeting, mIdx) => (
            <div key={meeting.id}>
              {(meeting.publishStatus === "published" ||
                meeting.publishStatus === "completed") && (
                <div className="">
                  <div
                    className={clsx(
                      mIdx % 2 !== 0
                        ? "bg-gray-50/50 border-gray-300"
                        : "bg-gray-100 border-gray-200",
                      "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200",
                    )}
                  >
                    <div className="col-span-2">
                      <p className="text-sm font-medium text-black">
                        {meeting.meetingType}
                      </p>
                      <p className="mt-1 flex items-center text-sm text-gray-900">
                        <span className="truncate">
                          {meeting.room.roomName}
                        </span>
                      </p>
                      <p className="mt-1 flex items-center text-sm text-gray-900">
                        <span className="truncate">{meeting.scheduleDate}</span>
                      </p>
                      {platform?.showDuration && (
                        <p className="mt-1 flex items-center text-sm text-gray-900">
                          <span className="truncate">{meeting.duration}</span>
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 justify-end">
                      <div className="cursor-pointer">
                        {meeting.noticePdfUrl && (
                          <a
                            href={meeting.noticePdfUrl}
                            className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Notice
                          </a>
                        )}
                      </div>

                      {platform?.allowSupplemental &&
                        meeting.attachments?.length > 0 && (
                          <Tippy
                            content="Supplemental Materials"
                            placement="top"
                            animation="scale"
                            delay={[500, 0]}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSupplemental({
                                  visible: true,
                                  meeting: meeting,
                                });
                              }}
                              className="cursor-pointer"
                              aria-label="Supplemental Materials"
                            >
                              <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                            </button>
                          </Tippy>
                        )}
                      {meeting.videoUrl && (
                        <>
                          {meeting.pdfOnly ? (
                            <a
                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer"
                            >
                              Watch
                            </a>
                          ) : (
                            <a
                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer"
                            >
                              Watch
                            </a>
                          )}
                        </>
                      )}
                      {meeting.hasLiveFeed && (
                        <>
                          {meeting.pdfOnly ? (
                            <a
                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/live/${client}/${meeting.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer"
                            >
                              Watch
                            </a>
                          ) : (
                            <a
                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/live-player/${client}/${meeting.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer"
                            >
                              Watch
                            </a>
                          )}
                        </>
                      )}
                      {meeting.pdfOnly ? (
                        <Link
                          href="/engage/[client]/agenda-pdf/[agenda]"
                          as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                            Agenda
                          </a>
                        </Link>
                      ) : (
                        <Link
                          href="/engage/[client]/[agenda]"
                          as={`/engage/${client}/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                            Agenda
                          </a>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {meeting.publishStatus === "minutes" && (
                <div className="">
                  <div
                    className={clsx(
                      mIdx % 2 !== 0
                        ? "bg-gray-50/50 border-gray-300"
                        : "bg-gray-100 border-gray-200",
                      "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200",
                    )}
                  >
                    <div className="col-span-2">
                      <p className="text-sm font-medium text-black">
                        {meeting.meetingType}
                      </p>
                      <p className="mt-1 flex items-center text-sm text-gray-900">
                        <span className="truncate">
                          {meeting.room.roomName}
                        </span>
                      </p>
                      <p className="mt-1 flex items-center text-sm text-gray-900">
                        <span className="truncate">{meeting.scheduleDate}</span>
                      </p>
                    </div>
                    <div className="flex items-center justify-end mt-0.5 space-x-2">
                      <div className="cursor-pointer">
                        {meeting.noticePdfUrl && (
                          <a
                            href={meeting.noticePdfUrl}
                            className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Notice
                          </a>
                        )}
                      </div>
                      {platform?.allowSupplemental &&
                        meeting.attachments?.length > 0 && (
                          <Tippy
                            content="Supplemental Materials"
                            placement="top"
                            animation="scale"
                            delay={[500, 0]}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSupplemental({
                                  visible: true,
                                  meeting: meeting,
                                });
                              }}
                              className="cursor-pointer"
                              aria-label="Supplemental Materials"
                            >
                              <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                            </button>
                          </Tippy>
                        )}

                      {meeting.videoUrl && (
                        <>
                          {meeting.pdfOnly ? (
                            <a
                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer"
                            >
                              Watch
                            </a>
                          ) : (
                            <a
                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer"
                            >
                              Watch
                            </a>
                          )}
                        </>
                      )}
                      {meeting.pdfOnly ? (
                        <Link
                          href="/engage/[client]/agenda-pdf/[agenda]"
                          as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                            Agenda
                          </a>
                        </Link>
                      ) : (
                        <Link
                          href="/engage/[client]/[agenda]"
                          as={`/engage/${client}/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                            Agenda
                          </a>
                        </Link>
                      )}
                      {meeting.minutesPdfOnly ? (
                        <Link
                          href="/engage/[client]/minutes-pdf/[agenda]"
                          as={`/engage/${client}/minutes-pdf/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                            Minutes
                          </a>
                        </Link>
                      ) : (
                        <Link
                          href="/engage/[client]/minutes/[minutes]"
                          as={`/engage/${client}/minutes/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                            Minutes
                          </a>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {meeting.publishStatus === "manualminutes" && (
                <div className="">
                  <div
                    className={clsx(
                      mIdx % 2 !== 0
                        ? "bg-gray-50/50 border-gray-300"
                        : "bg-gray-100 border-gray-200",
                      "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200",
                    )}
                  >
                    <div className="col-span-2">
                      <p className="text-sm font-medium text-black">
                        {meeting.meetingType}
                      </p>
                      <p className="mt-1 flex items-center text-sm text-gray-900">
                        <span className="truncate">
                          {meeting.room.roomName}
                        </span>
                      </p>
                      <p className="mt-1 flex items-center text-sm text-gray-900">
                        <span className="truncate">{meeting.scheduleDate}</span>
                      </p>
                    </div>
                    <div className="flex items-center justify-end mt-0.5 space-x-2">
                      <div className="cursor-pointer">
                        {meeting.noticePdfUrl && (
                          <a
                            href={meeting.noticePdfUrl}
                            className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Notice
                          </a>
                        )}
                      </div>
                      {platform?.allowSupplemental &&
                        meeting.attachments?.length > 0 && (
                          <Tippy
                            content="Supplemental Materials"
                            placement="top"
                            animation="scale"
                            delay={[500, 0]}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSupplemental({
                                  visible: true,
                                  meeting: meeting,
                                });
                              }}
                              className="cursor-pointer"
                              aria-label="Supplemental Materials"
                            >
                              <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                            </button>
                          </Tippy>
                        )}

                      {meeting.videoUrl && (
                        <>
                          {meeting.pdfOnly ? (
                            <a
                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer"
                            >
                              Watch
                            </a>
                          ) : (
                            <a
                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer"
                            >
                              Watch
                            </a>
                          )}
                        </>
                      )}
                      {meeting.pdfOnly ? (
                        <Link
                          href="/engage/[client]/agenda-pdf/[agenda]"
                          as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                            Agenda
                          </a>
                        </Link>
                      ) : (
                        <Link
                          href="/engage/[client]/[agenda]"
                          as={`/engage/${client}/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                            Agenda
                          </a>
                        </Link>
                      )}
                      {meeting.minutesPdfOnly ? (
                        <Link
                          href="/engage/[client]/minutes-pdf/[agenda]"
                          as={`/engage/${client}/minutes-pdf/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                            Minutes
                          </a>
                        </Link>
                      ) : (
                        <Link
                          href="/engage/[client]/minutes/[minutes]"
                          as={`/engage/${client}/minutes/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                            Minutes
                          </a>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {(meeting.publishStatus === "draft" ||
                meeting.publishStatus === "draftreview") &&
                client !== "broomfield" && (
                  <div
                    className={clsx(
                      mIdx % 2 !== 0
                        ? "bg-gray-50/50 border-gray-300"
                        : "bg-gray-100 border-gray-200",
                      "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm transition duration-200",
                    )}
                  >
                    <div className="col-span-2">
                      <p className="text-sm font-medium text-black">
                        {meeting.meetingType}
                      </p>
                      <p className="mt-1 flex items-center text-sm text-gray-900">
                        <span className="truncate">
                          {meeting.room.roomName}
                        </span>
                      </p>
                      <p className="mt-1 flex items-center text-sm text-gray-900">
                        <span className="truncate">{meeting.scheduleDate}</span>
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 justify-end">
                      <div className="cursor-pointer">
                        {meeting.noticePdfUrl && (
                          <a
                            href={meeting.noticePdfUrl}
                            className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Notice
                          </a>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-black text-right">
                        Agenda Pending
                      </p>
                    </div>
                  </div>
                )}
              {meeting.publishStatus === "draftreview" &&
                client === "broomfield" && (
                  <div className="">
                    <div
                      className={clsx(
                        mIdx % 2 !== 0
                          ? "bg-gray-50/50 border-gray-300"
                          : "bg-gray-100 border-gray-200",
                        "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200",
                      )}
                    >
                      <div className="col-span-2">
                        <p className="text-sm font-medium text-black">
                          {meeting.meetingType}
                        </p>
                        <p className="mt-1 flex items-center text-sm text-gray-900">
                          <span className="truncate">
                            {meeting.room.roomName}
                          </span>
                        </p>
                        <p className="mt-1 flex items-center text-sm text-gray-900">
                          <span className="truncate">
                            {meeting.scheduleDate}
                          </span>
                        </p>
                        {platform?.showDuration && (
                          <p className="mt-1 flex items-center text-sm text-gray-900">
                            <span className="truncate">{meeting.duration}</span>
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 justify-end">
                        <div className="cursor-pointer">
                          {meeting.noticePdfUrl && (
                            <a
                              href={meeting.noticePdfUrl}
                              className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                              target="_blank"
                              rel="noreferrer"
                            >
                              Notice
                            </a>
                          )}
                        </div>
                        <Link
                          href="/engage/[id]/[agenda]"
                          as={`/engage/${client}/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                            Draft Available
                          </a>
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              {meeting.publishStatus === "review" && (
                <div>
                  <div
                    className={clsx(
                      mIdx % 2 !== 0
                        ? "bg-gray-50/50 border-gray-300"
                        : "bg-gray-100 border-gray-200",
                      "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200",
                    )}
                  >
                    <div className="col-span-2">
                      <p className="text-sm font-medium text-black">
                        {meeting.meetingType}
                      </p>
                      <p className="mt-1 flex items-center text-sm text-gray-900">
                        <span className="truncate">
                          {meeting.room.roomName}
                        </span>
                      </p>
                      <p className="mt-1 flex items-center text-sm text-gray-900">
                        <span className="truncate">{meeting.scheduleDate}</span>
                      </p>
                    </div>
                    <div className="flex items-center justify-end mt-0.5 space-x-2">
                      <div className="cursor-pointer">
                        {meeting.noticePdfUrl && (
                          <a
                            href={meeting.noticePdfUrl}
                            className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Notice
                          </a>
                        )}
                      </div>
                      {platform?.allowSupplemental &&
                        meeting.attachments?.length > 0 && (
                          <Tippy
                            content="Supplemental Materials"
                            placement="top"
                            animation="scale"
                            delay={[500, 0]}
                          >
                            <button
                              onClick={() =>
                                setSupplemental({
                                  visible: true,
                                  meeting: meeting,
                                })
                              }
                              className="cursor-pointer"
                              aria-label="Supplemental Materials"
                            >
                              <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                            </button>
                          </Tippy>
                        )}

                      {meeting.videoUrl && (
                        <>
                          {meeting.pdfOnly ? (
                            <a
                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer"
                            >
                              Watch
                            </a>
                          ) : (
                            <a
                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer"
                            >
                              Watch
                            </a>
                          )}
                        </>
                      )}
                      {meeting.pdfOnly ? (
                        <Link
                          href="/engage/[client]/agenda-pdf/[agenda]"
                          as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                            Agenda
                          </a>
                        </Link>
                      ) : (
                        <Link
                          href="/engage/[client]/[agenda]"
                          as={`/engage/${client}/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-700 hover:text-blue-800 underline cursor-pointer">
                            Agenda
                          </a>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {(meeting.publishStatus === "canceled" ||
                meeting.publishStatus === "adjourned") && (
                <div>
                  <div
                    className={clsx(
                      mIdx % 2 !== 0
                        ? "bg-gray-50/50 border-gray-300"
                        : "bg-gray-100 border-gray-200",
                      "grid grid-cols-3 px-4 py-4 mb-4 border rounded-md shadow-sm hover:border-blue-500 hover:shadow-lg transition duration-200",
                    )}
                  >
                    <div className="col-span-2">
                      <p className="text-sm font-medium text-black">
                        {meeting.meetingType}
                      </p>
                      <p className="mt-1 flex items-center text-sm text-gray-900">
                        <span className="truncate">
                          {meeting.room.roomName}
                        </span>
                      </p>
                      <p className="mt-1 flex items-center text-sm text-gray-900">
                        <span className="truncate">{meeting.scheduleDate}</span>
                      </p>
                    </div>
                    <div className="flex items-center justify-end mt-0.5 space-x-2">
                      <div className="cursor-pointer">
                        {meeting.noticePdfUrl && (
                          <a
                            href={meeting.noticePdfUrl}
                            className="ml-1 flex px-3 py-0.5 text-sm cursor-pointer underline text-blue-700 hover:text-blue-800 transform duration-300"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Notice
                          </a>
                        )}
                      </div>
                      {platform?.allowSupplemental &&
                        meeting.attachments?.length > 0 && (
                          <Tippy
                            content="Supplemental Materials"
                            placement="top"
                            animation="scale"
                            delay={[500, 0]}
                          >
                            <button
                              onClick={() =>
                                setSupplemental({
                                  visible: true,
                                  meeting: meeting,
                                })
                              }
                              className="cursor-pointer"
                              aria-label="Supplemental Materials"
                            >
                              <HiOutlineDocumentDuplicate className="h-5 w-5 text-black hover:text-blue-800 transition duration-150" />
                            </button>
                          </Tippy>
                        )}
                      {meeting.pdfOnly ? (
                        <Link
                          href="/engage/[client]/agenda-pdf/[agenda]"
                          as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-red-600 hover:text-indigo-900 cursor-pointer select-none">
                            Meeting{" "}
                            {meeting.publishStatus === "canceled"
                              ? "Canceled"
                              : "Adjourned"}
                          </a>
                        </Link>
                      ) : (
                        <Link
                          href="/engage/[client]/cancelled/[id]"
                          as={`/engage/${client}/cancelled/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-red-600 hover:text-indigo-900 cursor-pointer select-none">
                            Meeting{" "}
                            {meeting.publishStatus === "canceled"
                              ? "Canceled"
                              : "Adjourned"}
                          </a>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Mobile Data */}
      <div className="sm:hidden overflow-hidden sm:rounded-md mt-2">
        <div>
          {!fetching && (
            <div className="my-4">
              <div className="pl-2 whitespace-nowrap text-sm font-medium text-black">
                <div className="flex items-center">
                  <MdFilterList className="h-4 w-4 text-black" />
                  <input
                    type="search"
                    value={filterText?.board}
                    onChange={(e) =>
                      applyFilter({
                        type: "FILTER_BOARD",
                        payload: e.target.value,
                      })
                    }
                    className="w-full bg-transparent text-black text-sm placeholder:text-zinc-400 border border-b-blue-600 border-t-transparent border-r-transparent border-l-transparent focus:outline-none active:outline-none"
                    placeholder="Filter by Type"
                  />
                </div>
              </div>
              <div className="pl-2 whitespace-nowrap text-sm font-medium text-black mt-3">
                <div className="flex items-center">
                  <MdFilterList className="h-4 w-4 text-black" />
                  <input
                    type="search"
                    value={filterText?.date}
                    onChange={(e) =>
                      applyFilter({
                        type: "FILTER_DATE",
                        payload: e.target.value,
                      })
                    }
                    className="w-full bg-transparent text-black text-sm placeholder:text-zinc-400 border border-b-blue-600 border-t-transparent border-r-transparent border-l-transparent focus:outline-none active:outline-none"
                    placeholder="Filter by Date"
                  />
                </div>
              </div>
            </div>
          )}
          {filteredMeetings?.map((meeting) => (
            <div key={meeting.id}>
              {(meeting.publishStatus === "published" ||
                meeting.publishStatus === "completed") && (
                <>
                  <div className="">
                    <div className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                      <div>
                        <p className="text-sm font-medium text-black">
                          {meeting.meetingType}
                        </p>
                        <p className="mt-1 flex items-center text-sm text-gray-900">
                          <span className="truncate">
                            {meeting.scheduleDate}
                          </span>
                        </p>
                      </div>
                      <div className="flex items-center">
                        {meeting.noticePdfUrl && (
                          <div className="cursor-pointer mr-2">
                            <a
                              href={meeting.noticePdfUrl}
                              className="flex py-0.5 text-sm cursor-pointer underline text-blue-800 hover:text-blue-900 transform duration-300 font-medium"
                              target="_blank"
                              rel="noreferrer"
                            >
                              Notice
                            </a>
                          </div>
                        )}
                        {meeting.hasLiveFeed && (
                          <>
                            {meeting.pdfOnly ? (
                              <a
                                href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/live/${client}/${meeting.id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline cursor-pointer mr-2 font-medium"
                              >
                                Watch
                              </a>
                            ) : (
                              <a
                                href={`${process.env.NEXT_PUBLIC_BASE_URL}/live-player/${client}/${meeting.id}`}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline cursor-pointer mr-2 font-medium"
                              >
                                Watch
                              </a>
                            )}
                          </>
                        )}
                        {meeting.videoUrl && (
                          <>
                            {meeting.pdfOnly ? (
                              <a
                                href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                                className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline cursor-pointer mr-2 font-medium"
                                target="_blank"
                                rel="noreferrer"
                              >
                                Watch
                              </a>
                            ) : (
                              <a
                                href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                                className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline cursor-pointer mr-2 font-medium"
                                target="_blank"
                                rel="noreferrer"
                              >
                                Watch
                              </a>
                            )}
                          </>
                        )}
                        {meeting.pdfOnly ? (
                          <Link
                            href="/engage/[client]/agenda-pdf/[agenda]"
                            as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                          >
                            <a className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline font-medium">
                              Agenda
                            </a>
                          </Link>
                        ) : (
                          <Link
                            href="/engage/[client]/[agenda]"
                            as={`/engage/${client}/${meeting.id}`}
                          >
                            <a className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline font-medium">
                              Agenda
                            </a>
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
              {meeting.publishStatus === "minutes" && (
                <div>
                  <div className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                    <div>
                      <p className="text-sm font-medium text-black">
                        {meeting.meetingType}
                      </p>
                      <p className="mt-1 flex items-center text-sm text-gray-900">
                        <span className="truncate">{meeting.scheduleDate}</span>
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {meeting.noticePdfUrl && (
                        <div className="cursor-pointer mr-2">
                          <a
                            href={meeting.noticePdfUrl}
                            className="flex py-0.5 text-sm cursor-pointer underline text-blue-800 hover:text-blue-900 transform duration-300 font-medium"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Notice
                          </a>
                        </div>
                      )}
                      {meeting.videoUrl && (
                        <>
                          {meeting.pdfOnly ? (
                            <a
                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                              className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline cursor-pointer mr-2 font-medium"
                              target="_blank"
                              rel="noreferrer"
                            >
                              Watch
                            </a>
                          ) : (
                            <a
                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                              className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline cursor-pointer mr-2 font-medium"
                              target="_blank"
                              rel="noreferrer"
                            >
                              Watch
                            </a>
                          )}
                        </>
                      )}
                      {meeting.pdfOnly ? (
                        <Link
                          href="/engage/[client]/agenda-pdf/[agenda]"
                          as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline cursor-pointer mr-2 font-medium">
                            Agenda
                          </a>
                        </Link>
                      ) : (
                        <Link
                          href="/engage/[client]/[agenda]"
                          as={`/engage/${client}/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline cursor-pointer mr-2 font-medium">
                            Agenda
                          </a>
                        </Link>
                      )}
                      {meeting.minutesPdfOnly ? (
                        <Link
                          href="/engage/[client]/minutes-pdf/[agenda]"
                          as={`/engage/${client}/minutes-pdf/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline cursor-pointer font-medium">
                            Minutes
                          </a>
                        </Link>
                      ) : (
                        <Link
                          href="/engage/[client]/minutes/[minutes]"
                          as={`/engage/${client}/minutes/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline cursor-pointer font-medium">
                            Minutes
                          </a>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {meeting.publishStatus === "manualminutes" && (
                <div>
                  <div className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                    <div>
                      <p className="text-sm font-medium text-black">
                        {meeting.meetingType}
                      </p>
                      <p className="mt-1 flex items-center text-sm text-gray-900">
                        <span className="truncate">{meeting.scheduleDate}</span>
                      </p>
                    </div>
                    <div className="flex items-center">
                      {meeting.noticePdfUrl && (
                        <div className="cursor-pointer mr-2">
                          <a
                            href={meeting.noticePdfUrl}
                            className="flex py-0.5 text-sm cursor-pointer underline text-blue-800 hover:text-blue-900 transform duration-300 font-medium"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Notice
                          </a>
                        </div>
                      )}
                      {meeting.videoUrl && (
                        <>
                          {meeting.pdfOnly ? (
                            <a
                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                              className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline cursor-pointer mr-2 font-medium"
                              target="_blank"
                              rel="noreferrer"
                            >
                              Watch
                            </a>
                          ) : (
                            <a
                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                              className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline cursor-pointer mr-2 font-medium"
                              target="_blank"
                              rel="noreferrer"
                            >
                              Watch
                            </a>
                          )}
                        </>
                      )}
                      {meeting.pdfOnly ? (
                        <Link
                          href="/engage/[client]/agenda-pdf/[agenda]"
                          as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline mr-2 font-medium">
                            Agenda
                          </a>
                        </Link>
                      ) : (
                        <Link
                          href="/engage/[client]/[agenda]"
                          as={`/engage/${client}/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-800 hover:text-blue-900underline mr-2 font-medium">
                            Agenda
                          </a>
                        </Link>
                      )}
                      {meeting.minutesPdfOnly ? (
                        <Link
                          href="/engage/[client]/minutes-pdf/[agenda]"
                          as={`/engage/${client}/minutes-pdf/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline font-medium">
                            Minutes
                          </a>
                        </Link>
                      ) : (
                        <Link
                          href="/engage/[client]/minutes/[minutes]"
                          as={`/engage/${client}/minutes/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline font-medium">
                            Minutes
                          </a>
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {(meeting.publishStatus === "draft" ||
                meeting.publishStatus === "draftreview") &&
                client !== "broomfield" && (
                  <div className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                    <div>
                      <p className="text-sm font-medium text-black">
                        {meeting.meetingType}
                      </p>
                      <p className="mt-1 flex items-center text-sm text-gray-900">
                        <span className="truncate">{meeting.scheduleDate}</span>
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {meeting.noticePdfUrl && (
                        <div className="cursor-pointer mr-2">
                          <a
                            href={meeting.noticePdfUrl}
                            className="flex py-0.5 text-sm cursor-pointer underline text-blue-800 hover:text-blue-900 transform duration-300 font-medium"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Notice
                          </a>
                        </div>
                      )}
                      <p className="mt-1 text-sm text-black">Agenda Pending</p>
                    </div>
                  </div>
                )}
              {meeting.publishStatus === "draftreview" &&
                client === "broomfield" && (
                  <>
                    <div className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                      <div>
                        <p className="text-sm font-medium text-black">
                          {meeting.meetingType}
                        </p>
                        <p className="mt-1 flex items-center text-sm text-gray-900">
                          <span className="truncate">
                            {meeting.scheduleDate}
                          </span>
                        </p>
                      </div>
                      <div className="flex items-center">
                        {meeting.noticePdfUrl && (
                          <div className="cursor-pointer mr-2">
                            <a
                              href={meeting.noticePdfUrl}
                              className="flex py-0.5 text-sm cursor-pointer underline text-blue-800 hover:text-blue-900 transform duration-300 font-medium"
                              target="_blank"
                              rel="noreferrer"
                            >
                              Notice
                            </a>
                          </div>
                        )}
                        <Link
                          href="/engage/[id]/[agenda]"
                          as={`/engage/${client}/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline font-medium">
                            Draft Available
                          </a>
                        </Link>
                      </div>
                    </div>
                  </>
                )}

              {meeting.publishStatus === "review" && (
                <>
                  <div className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                    <div>
                      <p className="text-sm font-medium text-black">
                        {meeting.meetingType}
                      </p>
                      <p className="mt-1 flex items-center text-sm text-gray-900">
                        <span className="truncate">{meeting.scheduleDate}</span>
                      </p>
                    </div>
                    <div className="flex items-center">
                      {meeting.noticePdfUrl && (
                        <div className="cursor-pointer mr-2">
                          <a
                            href={meeting.noticePdfUrl}
                            className="flex py-0.5 text-sm cursor-pointer underline text-blue-800 hover:text-blue-900 transform duration-300 font-medium"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Notice
                          </a>
                        </div>
                      )}
                      {meeting.videoUrl && (
                        <>
                          {meeting.pdfOnly ? (
                            <a
                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/tv/basic/vod/${client}/${meeting.id}`}
                              className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline cursor-pointer mr-2 font-medium"
                              target="_blank"
                              rel="noreferrer"
                            >
                              Watch
                            </a>
                          ) : (
                            <a
                              href={`${process.env.NEXT_PUBLIC_BASE_URL}/player/${client}/${meeting.id}`}
                              className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline cursor-pointer mr-2 font-medium"
                              target="_blank"
                              rel="noreferrer"
                            >
                              Watch
                            </a>
                          )}
                        </>
                      )}
                      {meeting.pdfOnly ? (
                        <Link
                          href="/engage/[client]/agenda-pdf/[agenda]"
                          as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline font-medium">
                            Agenda
                          </a>
                        </Link>
                      ) : (
                        <Link
                          href="/engage/[client]/[agenda]"
                          as={`/engage/${client}/${meeting.id}`}
                        >
                          <a className="mt-1 text-sm text-blue-800 hover:text-blue-900 underline font-medium">
                            Agenda
                          </a>
                        </Link>
                      )}
                    </div>
                  </div>
                </>
              )}
              {(meeting.publishStatus === "canceled" ||
                meeting.publishStatus === "adjourned") && (
                <div className="grid sm:grid-cols-2 px-4 py-4 bg-gray-100 mb-4 border border-gray-200 rounded-[20px]">
                  <div>
                    <p className="text-sm font-medium text-black">
                      {meeting.meetingType}
                    </p>
                    <p className="mt-1 flex items-center text-sm text-gray-900">
                      <span className="truncate">{meeting.scheduleDate}</span>
                    </p>
                  </div>
                  <div className="flex items-center">
                    {meeting.noticePdfUrl && (
                      <div className="cursor-pointer mr-2">
                        <a
                          href={meeting.noticePdfUrl}
                          className="flex py-0.5 text-sm cursor-pointer underline text-blue-800 hover:text-blue-900 transform duration-300 font-medium"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Notice
                        </a>
                      </div>
                    )}
                    {meeting.pdfOnly ? (
                      <Link
                        href="/engage/[client]/agenda-pdf/[agenda]"
                        as={`/engage/${client}/agenda-pdf/${meeting.id}`}
                      >
                        <a className="mt-1 text-sm text-red-600 font-medium">
                          Meeting{" "}
                          {meeting.publishStatus === "canceled"
                            ? "Canceled"
                            : "Adjourned"}
                        </a>
                      </Link>
                    ) : (
                      <Link
                        href="/engage/[client]/cancelled/[id]"
                        as={`/engage/${client}/cancelled/${meeting.id}`}
                      >
                        <a className="mt-1 text-sm text-red-600 font-medium">
                          Meeting{" "}
                          {meeting.publishStatus === "canceled"
                            ? "Canceled"
                            : "Adjourned"}
                        </a>
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {supplemental?.visible && (
        <Supplemental
          meeting={supplemental?.meeting}
          update={setSupplemental}
        />
      )}
      <UserWayWidget />
    </div>
  );
};

export default Agendas;
